import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Edit2,
  Plus,
  Search,
  Save,
  Check,
  CircleMinus,
  CircleCheck,
  Shield,
  AlertTriangle,
  FileText,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  X,
  Lock,
} from "lucide-react";
import {
  governanceProcessesService,
  governanceProcessStepsService,
  governanceStakeholdersService,
  ENGAGEMENT_NATURES,
  type EngagementNature,
  type StepType,
  type StepAuthority,
  type StepAuditLink,
  sopVersioningService,
  SOP_DRAFT_WF_KIND,
  type SopDraft,
  type SopChange,
} from "../../../services/governanceService";
import { procedureTemplateService, type ProcedureTemplate } from "../../../services/auditService";
import controlRegisterService, {
  type ControlRegister,
  type StepLinkedControl,
  type ControlLinkedRisk,
  type ControlImplementationStep,
} from "../../../services/controlRegisterService";
import RiskVisualization from "../../risk/RiskVisualization";
import { authorityApi } from "../../../services/authorityApi";
import FormControl from "../../common/FormControl";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import Pagination from "../../common/Pagination";
import Toast, { ToastMessage } from "../../common/Toast";
import { confirmWarning, confirmActive, confirmDelete } from "../../common/confirm";
import SopStepsPanel, { type SopStepsPanelHandle } from "./SopStepsPanel";
import SopStakeholdersPanel from "./SopStakeholdersPanel";
import SopVersionsPanel, { SopChangesList } from "./SopVersionsPanel";
import TemplateCadenceTab from "../../cadence/TemplateCadenceTab";
import TemplateProgramsTab from "../../cadence/TemplateProgramsTab";
import NoDataFound from "../../common/NoDataFound";
import StatusSwitch from "../../common/StatusSwitch";
import Skeleton from "../../common/Skeleton";
import ApprovalDot, { WorkflowState } from "../../common/ApprovalDot";
import WorkflowModal from "../../common/WorkflowModal";
import { usePermissions, usePermission } from "../../../contexts/PermissionContext";
import { useEntitySettings } from "../../../contexts/EntitySettingsContext";

const ACT_SOPS = "GOV_SOPS";
const WF_KIND = "gov_sop";

// Migration 114 — UTL_entity_registry id for SOP (gov_processes).
const SOP_TYPE_ID = 3;
const SOP_TYPE_CODE = "SOP";
const SOP_DISPLAY = "SOP / Process";

// Step types after migration 069. EXECUTE was renamed to PERFORM; VERIFY was
// dropped (advisory review uses REVIEW, no-authority steps use OTHER).
const STEP_TYPES = [
  "PERFORM",
  "APPROVE",
  "REVIEW",
  "NOTIFY",
  "OTHER",
] as const;
const TIMELINE_ANCHORS: Array<{ value: string; label: string }> = [
  { value: "AFTER_PREV_END", label: "after end of previous step" },
  { value: "AFTER_PREV_START", label: "after start of previous step" },
  { value: "FIXED", label: "fixed (calendar) days" },
];

const STEP_TYPE_OPTIONS = STEP_TYPES.map((t) => ({ value: t, label: t }));

const ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: "PERFORM", label: "Performs" },
  { value: "APPROVE", label: "Approves" },
  { value: "REVIEW",  label: "Reviews" },
  { value: "NOTIFY",  label: "Is notified" },
  { value: "OTHER",   label: "Other" },
];

// Walkthrough-family types cause circular SOP→step→Walkthrough→SOP links — exclude them.
const AUDIT_LINK_EXCLUDED = new Set(["Walkthrough", "WalkthroughDesign", "WalkthroughCompliance"]);

const PROCEDURE_TYPE_LABEL: Record<string, string> = {
  Checklist: "Checklist",
  Questionnaire: "Questionnaire",
  Form: "Form",
  Rating: "Rating",
  Confirmation: "Confirmation",
  PhysicalCheck: "Physical Check",
  RiskAssessment: "Risk Assessment",
  ControlTest: "Control Test",
};

// Inline draft row used while adding/editing a step authority.
// `id` is the persisted GOV_process_step_authorities row id if known.
interface StepAuthorityDraft {
  id?: number | null;
  authority_id: number | null;
  role: StepType;
}

interface AuditLinkDraft {
  id: number | null;   // null = new (not yet persisted)
  procedure_template_id: number;
  procedure_name: string;
  procedure_type: string;
  authority_id: number | null;
}
const SOP_STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "APPROVED", label: "Approved" },
  { value: "RETIRED", label: "Retired" },
];

// --- Types ---

interface GovernanceProcess {
  id: number;
  process_code?: string;
  // System code (SOP-N) — assigned on approve, null until then.
  code?: string | null;
  process_name: string;
  description: string;
  owner: string;
  owner_authority_id?: number | null;
  // Server-resolved owner authority name (list query join).
  owner_authority_name?: string | null;
  version: string;
  /** Live version number, and the open draft of the next version (if any). */
  current_version_number?: number;
  draft_version_number?: number | null;
  draft_workflow_state?: "draft" | "submitted" | "rejected" | null;
  process_status: "DRAFT" | "APPROVED" | "RETIRED";
  // Maker-checker workflow state (separate from the legacy process_status).
  // Drives the traffic-light ApprovalDot.
  workflow_state?: WorkflowState;
  effective_date: string;
  review_cycle_months: number;
  // Derived server-side from active ADT walkthrough templates (gov_routes
  // _derive_sop_walkthrough_flags). Carried from the list row so the Audit
  // Enablement checkboxes reflect persisted state on open/reload.
  enable_test_of_design?: boolean;
  enable_test_of_compliance?: boolean;
  is_active: number;
  // High-level link stats, aggregated by the list query only. The single-SOP
  // endpoint does not compute them, so a SOP opened directly by id reports 0.
  authority_count?: number;
  control_count?: number;
  risk_count?: number;
}

interface Procedure {
  id: number;
  process_id: number;
  step_number: number;
  step_name: string;
  description: string;
  step_type?: string;
  after_step_id?: number | null;
  after_step_name?: string | null;
  timeline_days?: number | null;
  timeline_anchor?: string;
  authorities?: StepAuthority[];
  audit_links?: StepAuditLink[];
  is_active: number;
  /** Set on steps of a draft version: the draft id, and the live step it edits
   *  (null = step added in the draft). */
  draft_id?: number;
  live_step_id?: number | null;
}

/** The live step a step row stands for — itself for a live step, its live
 *  counterpart for a draft step (null when the step is new in the draft). Used
 *  for things that are not versioned: control links and audit procedure links. */
const liveIdOf = (p: Procedure): number | null =>
  p.draft_id != null ? (p.live_step_id ?? null) : p.id;

interface AuthorityLite {
  id: number;
  name: string;
}

// Control / Risk / Implementation shapes are imported from controlRegisterService
// (StepLinkedControl, ControlLinkedRisk, ControlImplementationStep). The legacy
// mock interfaces lived here previously.

// --- Sub-Components for 3-Column Layout ---

const RisksColumn = ({
  selectedControl,
  risks,
  loading,
  onRiskClick,
}: {
  selectedControl: StepLinkedControl | null;
  risks: ControlLinkedRisk[];
  loading: boolean;
  onRiskClick: (risk: ControlLinkedRisk) => void;
}) => {
  if (!selectedControl) {
    return (
      <div className="flex flex-col h-full bg-gray-50 border-r border-gray-200 items-center justify-center p-8 text-center text-gray-400">
        <AlertTriangle size={32} className="opacity-20 mb-2" />
        <p className="text-sm">Select a Control to view mitigated Risks.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 border-r border-gray-200 min-h-0">
      <div className="h-16 px-4 border-b border-gray-200 bg-white shadow-sm z-10 shrink-0 flex items-center">
        <div className="flex items-center gap-2 text-gray-700 font-bold text-lg">
          <AlertTriangle className="text-amber-500" size={20} />
          <h2>Mitigated Risks</h2>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <div
          className={`space-y-3 transition-all duration-300 ${selectedControl ? "bg-orange-50/50 p-3 rounded-xl border-2 border-orange-200" : ""}`}
        >
          {loading ? (
            <div className="text-center py-6 text-gray-400 text-sm italic">Loading…</div>
          ) : risks.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm italic">
              No risks linked to this control.
            </div>
          ) : (
            risks.map((risk) => (
              <div
                key={risk.id}
                onClick={() => onRiskClick(risk)}
                data-testid={`BTN_sop_step_risk_${risk.id}`}
                className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer hover:border-orange-300 group"
              >
                <div className="flex justify-between items-start mb-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded border bg-gray-50 text-gray-600 border-gray-200">
                    {risk.code}
                  </span>
                  <ArrowRight
                    size={12}
                    className="text-gray-300 group-hover:text-orange-500 transition-colors opacity-0 group-hover:opacity-100"
                  />
                </div>
                <div className="text-sm font-semibold text-gray-800">{risk.name}</div>
                {risk.description && (
                  <p className="text-xs text-gray-600 leading-snug mt-1 line-clamp-2">
                    {risk.description}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const ControlsColumn = ({
  controls,
  selectedControlId,
  loading,
  readOnly,
  onSelectControl,
  onAddControl,
  onUnlinkControl,
}: {
  controls: StepLinkedControl[];
  selectedControlId: number | undefined;
  loading: boolean;
  readOnly?: boolean;
  onSelectControl: (c: StepLinkedControl) => void;
  onAddControl: () => void;
  onUnlinkControl: (c: StepLinkedControl) => void;
}) => {
  const typeBadge = (type?: string | null) => {
    const v = (type || "").toUpperCase();
    if (v === "PREVENTIVE") return "bg-green-50 text-green-600 border-green-100";
    if (v === "DETECTIVE") return "bg-purple-50 text-purple-600 border-purple-100";
    if (v === "CORRECTIVE") return "bg-orange-50 text-orange-600 border-orange-100";
    return "bg-gray-50 text-gray-500 border-gray-200";
  };
  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200 min-h-0">
      <div className="h-16 px-4 border-b border-gray-200 bg-white shadow-sm z-10 shrink-0 flex justify-between items-center">
        <div className="flex items-center gap-2 text-gray-700 font-bold text-lg">
          <Shield className="text-blue-600" size={20} />
          <h2>Controls</h2>
        </div>
        {!readOnly && (
          <button
            onClick={onAddControl}
            className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
            title="Link an existing control"
            data-testid="BTN_sop_step_add_control"
          >
            <Plus size={18} />
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4 bg-slate-50/30">
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8 text-gray-400 italic">Loading…</div>
          ) : controls.length === 0 ? (
            <div className="text-center py-8 text-gray-400 italic">
              No controls linked to this step{readOnly ? "." : ". Click + to link one."}
            </div>
          ) : (
            controls.map((control) => (
              <div
                key={control.id}
                onClick={() => onSelectControl(control)}
                data-testid={`BTN_sop_step_control_${control.id}`}
                className={`relative p-3 rounded-lg border cursor-pointer transition-all ${selectedControlId === control.id
                    ? "bg-white border-blue-500 shadow-md ring-1 ring-blue-100"
                    : "bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm"
                  }`}
              >
                {selectedControlId === control.id && (
                  <div className="absolute -left-2 top-1/2 -translate-y-1/2 bg-orange-500 text-white p-0.5 rounded-full shadow-sm z-20">
                    <ChevronLeft size={12} />
                  </div>
                )}
                <div className="flex justify-between items-start mb-1 gap-2">
                  <span className="text-xs font-bold text-gray-500">
                    {control.code}
                  </span>
                  <div className="flex items-center gap-1">
                    {control.type && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${typeBadge(control.type)}`}>
                        {control.type}
                      </span>
                    )}
                    {!readOnly && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onUnlinkControl(control); }}
                        className="text-gray-300 hover:text-rose-500 transition-colors"
                        title="Unlink control from step"
                        data-testid={`BTN_sop_step_control_${control.id}_unlink`}
                      >
                        <CircleMinus size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <h4 className="text-sm font-semibold text-gray-800 mb-1">
                  {control.name}
                </h4>
                {control.description && (
                  <p className="text-xs text-gray-600 line-clamp-2">
                    {control.description}
                  </p>
                )}
                {selectedControlId === control.id && (
                  <div className="absolute -right-2 top-1/2 -translate-y-1/2 bg-blue-500 text-white p-0.5 rounded-full shadow-sm z-20">
                    <ChevronRight size={12} />
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const ImplementationsColumn = ({
  selectedControl,
  implementations,
  loading,
}: {
  selectedControl: StepLinkedControl | null;
  implementations: ControlImplementationStep[];
  loading: boolean;
}) => {
  if (!selectedControl) {
    return (
      <div className="flex flex-col h-full bg-gray-50 items-center justify-center p-8 text-center text-gray-400">
        <FileText size={32} className="opacity-20 mb-2" />
        <p className="text-sm">Select a Control to view Implementations.</p>
      </div>
    );
  }

  const typeBadge = (t: string) => {
    if (t === "SOP") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (t === "IT_APPLICATION") return "bg-blue-50 text-blue-700 border-blue-200";
    if (t === "IT_ASSET") return "bg-purple-50 text-purple-700 border-purple-200";
    return "bg-gray-50 text-gray-600 border-gray-200";
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 min-h-0">
      <div className="h-16 px-4 border-b border-gray-200 bg-white shadow-sm z-10 shrink-0 flex items-center">
        <div className="flex items-center gap-2 text-gray-700 font-bold text-lg">
          <FileText className="text-purple-600" size={20} />
          <h2>Implementations</h2>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <div
          className={`space-y-3 transition-all duration-300 ${selectedControl ? "bg-blue-50/50 p-3 rounded-xl border-2 border-blue-200" : ""}`}
        >
          {loading ? (
            <div className="text-center py-6 text-gray-400 text-sm italic">Loading…</div>
          ) : implementations.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm italic">
              No implementations defined for this control.
            </div>
          ) : (
            implementations.map((impl) => (
              <div
                key={impl.id}
                data-testid={`ITEM_sop_step_impl_${impl.id}`}
                className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-1 gap-2">
                  <span className="text-xs font-bold text-gray-500">#{impl.sequence_number}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${typeBadge(impl.implementation_type)}`}>
                    {impl.implementation_type}
                  </span>
                </div>
                <div className="text-sm font-semibold text-gray-800">
                  {impl.target_label || `#${impl.target_id}`}
                </div>
                {impl.target_subtitle && (
                  <div className="text-xs text-gray-500 mt-0.5">{impl.target_subtitle}</div>
                )}
                {impl.remarks && (
                  <p className="text-xs text-gray-600 leading-snug mt-1">{impl.remarks}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---

/** Normalise one GOV_processes API row into the shape this screen renders.
 *  Shared by the list load and the single-SOP fetch used when auto-opening a
 *  linked SOP, so both paths produce identical objects. The link-count and
 *  owner-name fields are joins only the list query computes; the single-row
 *  endpoint omits them, so they default here rather than rendering undefined. */
const mapProcessRow = (p: any): GovernanceProcess => ({
  id: p.id,
  process_code: p.process_code || undefined,
  // System code (SOP-N), assigned on approve.
  code: p.code ?? null,
  process_name: p.process_name,
  description: p.description || "",
  owner: p.owner || "",
  owner_authority_id: p.owner_authority_id ?? null,
  // Server-resolved owner authority name (join). Falls back to the client
  // authorities lookup, then #id, so owners not in the approved dropdown
  // still render a name.
  owner_authority_name: p.owner_authority_name ?? null,
  // Version label follows the live version number (SOP versioning).
  version: String(p.current_version_number ?? p.version ?? "1"),
  current_version_number: p.current_version_number ?? 1,
  draft_version_number: p.draft_version_number ?? null,
  draft_workflow_state: p.draft_workflow_state ?? null,
  process_status: p.process_status || "DRAFT",
  workflow_state: (p.workflow_state || "draft") as WorkflowState,
  effective_date: p.effective_date || "",
  review_cycle_months: p.review_cycle_months || 12,
  // Carry the server-derived audit-enablement flags (backend attaches
  // them per row). They were dropped here, which made the Audit
  // Enablement checkboxes appear to untick on reload/reopen.
  enable_test_of_design: !!p.enable_test_of_design,
  enable_test_of_compliance: !!p.enable_test_of_compliance,
  is_active: typeof p.is_active === "number" ? p.is_active : 1,
  // High-level link stats from the list query (no N+1).
  authority_count: p.authority_count ?? 0,
  control_count: p.control_count ?? 0,
  risk_count: p.risk_count ?? 0,
});

interface GovernanceProcessesProps {
  initialSopId?: number | null;
  onClose?: () => void;
}

const GovernanceProcesses: React.FC<GovernanceProcessesProps> = ({
  initialSopId,
  onClose,
}) => {
  const { formatDate } = useEntitySettings();
  const [processes, setProcesses] = useState<GovernanceProcess[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  // Live data for the step drill-down (config modal). All three lists are
  // entity-scoped and resolved on demand when the modal opens / a control is
  // selected. No mock arrays anymore.
  const [stepControls, setStepControls] = useState<StepLinkedControl[]>([]);
  const [stepControlsLoading, setStepControlsLoading] = useState(false);
  const [controlRisks, setControlRisks] = useState<ControlLinkedRisk[]>([]);
  const [controlRisksLoading, setControlRisksLoading] = useState(false);
  const [controlImplementations, setControlImplementations] = useState<ControlImplementationStep[]>([]);
  const [controlImplementationsLoading, setControlImplementationsLoading] = useState(false);

  // "Add Control" picker — opens a sub-modal listing all entity controls.
  const [showControlPicker, setShowControlPicker] = useState(false);
  const [pickerControls, setPickerControls] = useState<ControlRegister[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerLinking, setPickerLinking] = useState<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [authorities, setAuthorities] = useState<AuthorityLite[]>([]);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  // Main table pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Procedures table pagination
  const [procPage, setProcPage] = useState(1);
  const [procPageSize, setProcPageSize] = useState(10);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  // Active/Inactive toggle — independent of the workflow status dropdown above.
  const [activeFilter, setActiveFilter] = useState<"ACTIVE" | "INACTIVE">("ACTIVE");
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProcess, setSelectedProcess] =
    useState<GovernanceProcess | null>(null);
  const [formData, setFormData] = useState<Partial<GovernanceProcess>>({});
  const [workflowRow, setWorkflowRow] = useState<GovernanceProcess | null>(null);

  const { check } = usePermissions();
  const { allowed: canView, loading: viewLoading } = usePermission(ACT_SOPS, "view");
  const canModify = check(ACT_SOPS, "view+modify").allowed;

  // SOP detail modal — single-tier flat tabs. Live SOP vs Draft SOP differ
  // ONLY in the step listing; basic details, audit enablement, and stakeholders
  // exist once each. Per-tab edit/save toggles keep tabs in read-only mode
  // by default; click Edit to enable, Save to persist.
  type SopTab = "basic" | "audit" | "stakeholders" | "live" | "draft" | "versions" | "cadence" | "programs";
  const [sopTab, setSopTab] = useState<SopTab>("basic");
  const [editingBasic, setEditingBasic] = useState(false);
  const [editingAudit, setEditingAudit] = useState(false);
  const [editingStakeholders, setEditingStakeholders] = useState(false);
  // Approved SOPs are locked — matches the Risk register / Agreements pattern:
  // show the approved banner, disable all fields, and hide the Edit + Inactive
  // buttons until the record is reopened via the workflow dot (approver acts there).
  const sopIsApproved =
    isEditMode &&
    !!selectedProcess &&
    (selectedProcess as any).workflow_state === "approved";
  // SOP versioning. Before its first approval a SOP is edited in place. Once
  // live (approved) it is locked; changes go into a draft of the next version
  // (its own maker-checker, WF kind gov_sop_draft) and approving that draft
  // makes it the live version. Past versions are on the Versions tab.
  const [sopDraft, setSopDraft] = useState<SopDraft | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftChanges, setDraftChanges] = useState<SopChange[] | null>(null);
  const [newVersionReason, setNewVersionReason] = useState("");
  const [draftWorkflowOpen, setDraftWorkflowOpen] = useState(false);
  const [versionsKey, setVersionsKey] = useState(0);
  const draftMode = sopIsApproved && !!sopDraft;
  const draftEditable =
    draftMode && (sopDraft!.workflow_state === "draft" || sopDraft!.workflow_state === "rejected");
  // Read by callbacks with stable identity (loadSteps etc.).
  const draftRef = useRef<SopDraft | null>(null);
  draftRef.current = draftMode ? sopDraft : null;
  // Basic Details are editable before first approval, or in an editable draft.
  const basicEditable = draftMode ? draftEditable : !sopIsApproved;
  // A step form may save if the step belongs to an editable draft, or is a live
  // step of a SOP that has not gone live yet.
  const stepEditable = (p: Procedure | null): boolean => {
    if (!p) return draftMode ? draftEditable : !sopIsApproved;
    return p.draft_id != null ? draftEditable : !sopIsApproved;
  };
  const draftStepsPanelRef = useRef<SopStepsPanelHandle | null>(null);
  const sopFormRef = useRef<HTMLFormElement | null>(null);
  const procFormRef = useRef<HTMLFormElement | null>(null);

  type ConfigTab = "edit" | "configure";
  const [configTab, setConfigTab] = useState<ConfigTab>("configure");
  const [editingProcBasic, setEditingProcBasic] = useState(false);

  const [showProcListModal, setShowProcListModal] = useState(false);
  const [showProcFormModal, setShowProcFormModal] = useState(false);
  const [activeProcessForProc, setActiveProcessForProc] =
    useState<GovernanceProcess | null>(null);
  const [selectedProc, setSelectedProc] = useState<Procedure | null>(null);
  const [procFormData, setProcFormData] = useState<Partial<Procedure>>({});
  const [isProcEditMode, setIsProcEditMode] = useState(false);
  const [procAuthorities, setProcAuthorities] = useState<StepAuthorityDraft[]>([]);
  const [procAuditLinks, setProcAuditLinks] = useState<AuditLinkDraft[]>([]);
  const [showProcDesc, setShowProcDesc] = useState(false);
  // Audit procedure search — server-side, tenant-scoped, debounced.
  const [auditSearchResults, setAuditSearchResults] = useState<ProcedureTemplate[]>([]);
  const [auditSearchLoading, setAuditSearchLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState("");
  const auditSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [activeProcedureForConfig, setActiveProcedureForConfig] =
    useState<Procedure | null>(null);
  const [selectedControl, setSelectedControl] = useState<StepLinkedControl | null>(null);

  const [showRiskVisModal, setShowRiskVisModal] = useState(false);
  const [selectedRiskForVis, setSelectedRiskForVis] = useState<ControlLinkedRisk | null>(
    null,
  );

  const loadProcesses = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      // Fetch all (active + inactive); the workflow_state status filter and the
      // active/inactive toggle are both applied client-side in filteredProcesses.
      const data = await governanceProcessesService.getAll({
        status: "ALL",
        search: searchTerm || undefined,
      });
      const mapped = (data as any[]).map(mapProcessRow);
      setProcesses(mapped);
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to load processes" });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, canView]);

  // Load the open draft of the next version (if any). While a draft is open the
  // Basic Details tab shows the draft's values, not the live ones.
  const loadDraft = useCallback(async (processId: number) => {
    setDraftLoading(true);
    try {
      const { draft } = await sopVersioningService.getDraft(processId);
      setSopDraft(draft);
      if (draft) {
        setFormData((prev) => ({
          ...prev,
          process_name: draft.process_name,
          description: draft.description || "",
          owner_authority_id: draft.owner_authority_id ?? null,
          effective_date: draft.effective_date || "",
          review_cycle_months: draft.review_cycle_months || 12,
        } as any));
      }
    } catch {
      setSopDraft(null);
    } finally {
      setDraftLoading(false);
    }
  }, []);

  useEffect(() => {
    setDraftChanges(null);
    setNewVersionReason("");
    if (showModal && isEditMode && selectedProcess?.id) loadDraft(selectedProcess.id);
    else setSopDraft(null);
  }, [showModal, isEditMode, selectedProcess?.id, loadDraft]);

  // Re-read the open SOP after a workflow action (approval, rollout, discard) so
  // the modal reflects its new state and version.
  const refreshSelectedProcess = useCallback(async () => {
    if (!selectedProcess) return;
    try {
      const row = mapProcessRow(await governanceProcessesService.getById(selectedProcess.id));
      setSelectedProcess(row);
      setFormData(row);
      await loadDraft(row.id);
    } catch {
      // list reload below still runs
    }
    setVersionsKey((k) => k + 1);
    await loadProcesses();
  }, [selectedProcess, loadDraft, loadProcesses]);

  useEffect(() => {
    loadProcesses();
    (async () => {
      try {
        const resp = await authorityApi.getAuthoritiesDropdown();
        const list = Array.isArray(resp?.items) ? resp.items : [];
        setAuthorities(list.map((a) => ({ id: a.id, name: a.name })));
      } catch {
        // non-critical
      }
    })();
  }, [loadProcesses]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, activeFilter, pageSize]);

  // Auto-open SOP detail modal when caller passes initialSopId — used when
  // navigating in from a linked SOP elsewhere (e.g. policy linked-SOP click).
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!initialSopId) return;
    if (loading) return;

    const openWith = (sop: GovernanceProcess) => {
      autoOpenedRef.current = true;
      setSelectedProcess(sop);
      setFormData(sop);
      setIsEditMode(true);
      setSaving(false);
      setToggling(false);
      setSopTab("live");
      setEditingBasic(false);
      setEditingAudit(false);
      setEditingStakeholders(false);
      setShowModal(true);
    };

    const match = processes.find((p) => p.id === Number(initialSopId));
    if (match) {
      openWith(match);
      return;
    }

    // Not in `processes` — the list endpoint is capped at 50 rows server-side
    // (LIMIT 50 ORDER BY id DESC), so any SOP older than the newest 50 is simply
    // absent from the array. Returning here is what made the linked-SOP click a
    // silent no-op. Fetch that one SOP by id instead.
    let cancelled = false;
    (async () => {
      try {
        const row = await governanceProcessesService.getById(Number(initialSopId));
        if (cancelled || autoOpenedRef.current) return;
        openWith(mapProcessRow(row));
      } catch (err: any) {
        if (cancelled) return;
        setToast({
          type: "error",
          message: err?.message || "Could not open that SOP",
        });
        if (onClose) onClose();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialSopId, processes, loading, onClose]);

  // When auto-opened via initialSopId and the inner Modal subsequently closes,
  // notify caller so it can unmount the overlay.
  useEffect(() => {
    if (!initialSopId) return;
    if (!autoOpenedRef.current) return;
    if (!showModal && onClose) onClose();
  }, [showModal, initialSopId, onClose]);

  useEffect(() => {
    setProcPage(1);
  }, [procPageSize, activeProcessForProc]);

  const loadSteps = useCallback(async (processId: number) => {
    try {
      setStepsLoading(true);
      const d = draftRef.current;
      const data = d
        ? await sopVersioningService.getDraftSteps(d.id)
        : await governanceProcessStepsService.getByProcess(processId);
      const mapped = (data as any[]).map((s: any) => ({
        id: s.id,
        process_id: s.process_id,
        step_number: s.step_number,
        step_name: s.step_name,
        description: s.description || "",
        step_type: s.step_type || "EXECUTE",
        after_step_id: s.after_step_id ?? null,
        after_step_name: s.after_step_name ?? null,
        timeline_days: s.timeline_days ?? null,
        timeline_anchor: s.timeline_anchor || "AFTER_PREV_END",
        authorities: (s.authorities || []) as StepAuthority[],
        audit_links: (s.audit_links || []) as StepAuditLink[],
        is_active: typeof s.is_active === "number" ? s.is_active : 1,
        draft_id: s.draft_id,
        live_step_id: s.live_step_id ?? null,
      }));
      setProcedures(mapped);
    } catch {
      setProcedures([]);
    } finally {
      setStepsLoading(false);
    }
  }, []);

  const filteredProcesses = processes.filter((p) => {
    const matchesSearch =
      !searchTerm ||
      p.process_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.description || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "ALL" ? true : (p.workflow_state || "draft") === statusFilter;
    const matchesActive =
      activeFilter === "ACTIVE" ? p.is_active === 1 : p.is_active !== 1;
    return matchesSearch && matchesStatus && matchesActive;
  });

  const totalRecords = filteredProcesses.length;
  const paginatedProcesses = filteredProcesses.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const handleAddNew = () => {
    setSelectedProcess(null);
    setFormData({
      process_name: "",
      description: "",
      owner_authority_id: null,
      effective_date: new Date().toISOString().split("T")[0],
      review_cycle_months: 12,
      enable_test_of_design: false,
      enable_test_of_compliance: false,
    } as any);
    setIsEditMode(false);
    setSopTab("basic");
    setEditingBasic(false);
    setEditingAudit(false);
    setEditingStakeholders(false);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEditMode && selectedProcess && draftMode && sopTab === "basic") {
        // Live SOP with an open draft: Basic Details edit the draft's copy.
        const updated = await sopVersioningService.updateDraft(sopDraft!.id, {
          process_name: formData.process_name,
          description: (formData as any).description,
          owner_authority_id: (formData as any).owner_authority_id,
          effective_date: formData.effective_date || null,
          review_cycle_months: formData.review_cycle_months,
        } as any);
        setSopDraft(updated);
        setDraftChanges(null);
        setToast({ type: "success", message: `Draft of version ${updated.target_version_number} updated.` });
        setEditingBasic(false);
      } else if (isEditMode && selectedProcess) {
        // Audit-enablement tab persists ONLY the walkthrough flags — a GRC-team mark
        // decoupled from the ownership-gated SOP edit (any GRC SOP-modify user may set
        // it, even on a SOP they didn't author). The Basic Details tab sends the SOP
        // columns. Scoping the payload per-tab keeps the backend on the correct gate
        // (flag-only → modify-extent; columns → ownership-gated edit).
        const payload = sopTab === "audit"
          ? {
              enable_test_of_design: (formData as any).enable_test_of_design,
              enable_test_of_compliance: (formData as any).enable_test_of_compliance,
            }
          : {
              process_name: formData.process_name,
              description: (formData as any).description,
              owner_authority_id: (formData as any).owner_authority_id,
              version: formData.version,
              process_status: formData.process_status,
              effective_date: formData.effective_date,
              review_cycle_months: formData.review_cycle_months,
            };
        await governanceProcessesService.update(selectedProcess.id, payload as any);
        setToast({ type: "success", message: sopTab === "audit" ? "Audit enablement updated." : "SOP updated successfully." });
        // Stay in the modal — just exit edit mode for the active section.
        if (sopTab === "basic") setEditingBasic(false);
        if (sopTab === "audit") setEditingAudit(false);
        await loadProcesses();
      } else {
        const created = await governanceProcessesService.create({
          process_name: formData.process_name,
          description: (formData as any).description,
          owner_authority_id: (formData as any).owner_authority_id,
          effective_date: formData.effective_date,
          review_cycle_months: formData.review_cycle_months,
          enable_test_of_design: (formData as any).enable_test_of_design,
          enable_test_of_compliance: (formData as any).enable_test_of_compliance,
        } as any);
        setToast({ type: "success", message: "SOP created. Add steps in the Draft SOP tab." });
        // Stay in the modal and jump straight to the Draft SOP tab so the user
        // can start adding steps without reopening anything.
        setSelectedProcess(created as any);
        setFormData(created as any);
        setIsEditMode(true);
        setSopTab("draft");
        await loadProcesses();
      }
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to save SOP.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!selectedProcess) return;
    const next = selectedProcess.is_active === 1 ? 0 : 1;
    const confirmFn = selectedProcess.is_active === 1 ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? "Reactivate this SOP?" : "Inactivate this SOP?",
      text: next
        ? "It will appear in active lists again."
        : "It will be hidden from active lists.",
      confirmText: next ? "Yes, Reactivate" : "Yes, Inactivate",
    });
    if (!ok) return;
    setToggling(true);
    try {
      await governanceProcessesService.update(selectedProcess.id, {
        is_active: next,
      } as any);
      setToast({
        type: next ? "success" : "warning",
        message: next ? "Reactivated." : "Inactivated.",
      });
      setShowModal(false);
      await loadProcesses();
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to update status.",
      });
    } finally {
      setToggling(false);
    }
  };

  const handleConfigure = (e: React.MouseEvent, process: GovernanceProcess) => {
    e.stopPropagation();
    // Row click opens the SOP detail modal directly on the Live SOP tab —
    // the read-only workflow view is what most users want first; Basic Details
    // / Audit / Stakeholders / Draft are one click away.
    setSelectedProcess(process);
    setFormData(process);
    setIsEditMode(true);
    setSaving(false);
    setToggling(false);
    setSopTab("live");
    setEditingBasic(false);
    setEditingAudit(false);
    setEditingStakeholders(false);
    setShowModal(true);
  };

  const handleAddProc = () => {
    setSelectedProc(null);
    setProcFormData({
      step_name: "",
      description: "",
      step_type: "PERFORM",
      after_step_id: null,
      timeline_days: null,
      timeline_anchor: "AFTER_PREV_END",
    } as any);
    setProcAuthorities([]);
    setProcAuditLinks([]);
    setShowProcDesc(false);
    setAuditSearch("");
    setIsProcEditMode(false);
    setSaving(false);
    setToggling(false);
    setAuditSearch("");
    setShowProcFormModal(true);
  };

  const handleEditProc = (proc: Procedure) => {
    setSelectedProc(proc);
    setProcFormData(proc);
    setProcAuthorities(
      (proc.authorities || []).map((a) => ({
        id: a.id,
        authority_id: a.authority_id,
        role: a.role as StepType,
      })),
    );
    setProcAuditLinks(
      (proc.audit_links || []).map((l) => ({
        id: l.id,
        procedure_template_id: l.procedure_template_id,
        procedure_name: l.procedure_name || "",
        procedure_type: l.procedure_type || "",
        authority_id: l.authority_id ?? null,
      })),
    );
    setShowProcDesc(false);
    setAuditSearch("");
    setIsProcEditMode(true);
    setSaving(false);
    setToggling(false);
    setAuditSearch("");
    setShowProcFormModal(true);
  };

  const addAuthorityRow = () => {
    setProcAuthorities((prev) => [
      ...prev,
      { id: null, authority_id: null, role: "PERFORM" },
    ]);
  };

  const updateAuthorityRow = (idx: number, patch: Partial<StepAuthorityDraft>) => {
    setProcAuthorities((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    );
  };

  const removeAuthorityRow = (idx: number) => {
    setProcAuthorities((prev) => prev.filter((_, i) => i !== idx));
  };

  // Debounced server-side search for audit procedures (tenant-scoped via lookup endpoint).
  useEffect(() => {
    if (auditSearchTimer.current) clearTimeout(auditSearchTimer.current);
    const term = auditSearch.trim();
    if (!term) { setAuditSearchResults([]); setAuditSearchLoading(false); return; }
    setAuditSearchLoading(true);
    auditSearchTimer.current = setTimeout(async () => {
      try {
        const results = await procedureTemplateService.lookup(term);
        setAuditSearchResults(results.filter((t) => !AUDIT_LINK_EXCLUDED.has(t.procedure_type)));
      } catch (e) {
        console.error("Audit procedure search failed", e);
        setAuditSearchResults([]);
      } finally {
        setAuditSearchLoading(false);
      }
    }, 300);
    return () => { if (auditSearchTimer.current) clearTimeout(auditSearchTimer.current); };
  }, [auditSearch]);

  const addAuditLink = (tpl: ProcedureTemplate) => {
    if (procAuditLinks.some((l) => l.procedure_template_id === tpl.id)) return;
    setProcAuditLinks((prev) => [
      ...prev,
      { id: null, procedure_template_id: tpl.id, procedure_name: tpl.procedure_name, procedure_type: tpl.procedure_type, authority_id: null },
    ]);
    setAuditSearch("");
  };

  const updateAuditLink = (idx: number, patch: Partial<AuditLinkDraft>) => {
    setProcAuditLinks((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeAuditLink = (idx: number) => {
    setProcAuditLinks((prev) => prev.filter((_, i) => i !== idx));
  };

  const syncStepAuditLinks = async (stepId: number, before: StepAuditLink[], after: AuditLinkDraft[]) => {
    const afterIds = new Set(after.map((r) => r.procedure_template_id));
    for (const orig of before) {
      if (!afterIds.has(orig.procedure_template_id)) {
        try { await governanceStakeholdersService.removeStepAuditLink(orig.id); }
        catch (e) { console.error("remove audit link failed", orig.id, e); }
      }
    }
    const beforeMap = new Map(before.map((r) => [r.procedure_template_id, r]));
    for (const row of after) {
      const existing = beforeMap.get(row.procedure_template_id);
      if (!existing) {
        try {
          await governanceStakeholdersService.addStepAuditLink({
            process_step_id: stepId,
            procedure_template_id: row.procedure_template_id,
            authority_id: row.authority_id ?? null,
          });
        } catch (e) { console.error("add audit link failed", row.procedure_template_id, e); }
      } else if (existing.authority_id !== row.authority_id) {
        try {
          await governanceStakeholdersService.updateStepAuditLink(existing.id, { authority_id: row.authority_id });
        } catch (e) { console.error("update audit link authority failed", existing.id, e); }
      }
    }
  };

  // Reconcile the inline authority list against what's persisted for the step.
  // - Rows present before but absent now (by id) → DELETE
  // - New rows with a valid authority_id → POST
  // - Rows whose role changed → PUT
  // Duplicate (authority_id, role) pairs are de-duped client-side;
  // backend also enforces the unique constraint.
  const syncStepAuthorities = async (
    stepId: number,
    before: StepAuthority[],
    after: StepAuthorityDraft[],
  ) => {
    const valid = after.filter((r) => r.authority_id != null);
    const seen = new Set<string>();
    const dedup: StepAuthorityDraft[] = [];
    for (const r of valid) {
      const key = `${r.authority_id}:${r.role}`;
      if (!seen.has(key)) { seen.add(key); dedup.push(r); }
    }

    const keptIds = new Set(dedup.filter((r) => r.id != null).map((r) => r.id!));
    for (const orig of before) {
      if (!keptIds.has(orig.id)) {
        try { await governanceStakeholdersService.removeStepAuthority(orig.id); }
        catch (e) { console.error("remove authority failed", orig.id, e); }
      }
    }
    for (const row of dedup) {
      if (row.id != null) {
        const orig = before.find((o) => o.id === row.id);
        if (orig && orig.role !== row.role) {
          await governanceStakeholdersService.updateStepAuthority(row.id, { role: row.role });
        }
      } else {
        const dup = before.some(
          (o) => o.authority_id === row.authority_id && o.role === row.role,
        );
        if (!dup) {
          await governanceStakeholdersService.addStepAuthority({
            step_id: stepId,
            authority_id: row.authority_id!,
            role: row.role,
          });
        }
      }
    }
  };

  const handleProcSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeProcessForProc) return;

    const stepType: string = (procFormData as any).step_type || "PERFORM";

    setSaving(true);
    try {
      const payload: any = {
        step_name: (procFormData as any).step_name,
        description: procFormData.description,
        step_type: stepType,
        after_step_id: (procFormData as any).after_step_id ?? null,
        timeline_days: (procFormData as any).timeline_days ?? null,
        timeline_anchor: (procFormData as any).timeline_anchor || "AFTER_PREV_END",
      };
      const auditBefore = (selectedProc?.audit_links || []) as StepAuditLink[];
      const d = draftRef.current;
      const editingDraftStep = isProcEditMode && selectedProc && selectedProc.draft_id != null;
      if (d && (editingDraftStep || !isProcEditMode)) {
        // Draft of the next version: the step and its authorities are saved to
        // the draft in one call. Audit procedure links are not versioned — they
        // stay on the live step, so they sync only when the step already exists live.
        const draftPayload = {
          ...payload,
          authorities: procAuthorities
            .filter((r) => r.authority_id != null)
            .map((r) => ({ authority_id: r.authority_id!, role: r.role })),
        };
        let liveId: number | null = null;
        if (editingDraftStep) {
          await sopVersioningService.updateDraftStep(d.id, selectedProc!.id, draftPayload);
          liveId = selectedProc!.live_step_id ?? null;
        } else {
          await sopVersioningService.createDraftStep(d.id, draftPayload);
        }
        if (liveId) await syncStepAuditLinks(liveId, auditBefore, procAuditLinks);
        setDraftChanges(null);
      } else {
        let savedStepId: number;
        if (isProcEditMode && selectedProc) {
          await governanceProcessStepsService.update(selectedProc.id, payload);
          savedStepId = selectedProc.id;
        } else {
          const created = await governanceProcessStepsService.create({
            process_id: activeProcessForProc.id,
            ...payload,
          });
          savedStepId = created.id;
        }

        // Sync authorities and audit links for this step.
        const before = selectedProc?.authorities || [];
        await syncStepAuthorities(savedStepId, before, procAuthorities);
        await syncStepAuditLinks(savedStepId, auditBefore, procAuditLinks);
      }

      setToast({
        type: "success",
        message: isProcEditMode ? "Step updated successfully." : "Step created successfully.",
      });
      if (isProcEditMode && showConfigModal) {
        setEditingProcBasic(false);
        setConfigTab("configure");
      } else {
        setShowProcFormModal(false);
      }
      await loadSteps(activeProcessForProc.id);
      await draftStepsPanelRef.current?.reload();
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to save step.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStepActive = async () => {
    if (!selectedProc) return;
    const next = selectedProc.is_active === 1 ? 0 : 1;
    const confirmFn = selectedProc.is_active === 1 ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? "Reactivate this step?" : "Inactivate this step?",
      text: next
        ? "It will appear in active lists again."
        : "It will be hidden from active lists.",
      confirmText: next ? "Yes, Reactivate" : "Yes, Inactivate",
    });
    if (!ok) return;
    setToggling(true);
    try {
      if (selectedProc.draft_id != null) {
        await sopVersioningService.updateDraftStep(selectedProc.draft_id, selectedProc.id, { is_active: next });
        setDraftChanges(null);
      } else {
        await governanceProcessStepsService.update(selectedProc.id, {
          is_active: next,
        } as any);
      }
      setToast({
        type: next ? "success" : "warning",
        message: next ? "Reactivated." : "Inactivated.",
      });
      setShowProcFormModal(false);
      setShowConfigModal(false);
      if (activeProcessForProc) await loadSteps(activeProcessForProc.id);
      await draftStepsPanelRef.current?.reload();
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to update status.",
      });
    } finally {
      setToggling(false);
    }
  };

  const loadStepControls = useCallback(async (stepId: number) => {
    try {
      setStepControlsLoading(true);
      const rows = await controlRegisterService.listStepControls(stepId);
      setStepControls(rows);
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to load controls for this step." });
      setStepControls([]);
    } finally {
      setStepControlsLoading(false);
    }
  }, []);

  const loadControlContext = useCallback(async (controlId: number) => {
    setControlRisks([]);
    setControlImplementations([]);
    setControlRisksLoading(true);
    setControlImplementationsLoading(true);
    try {
      const [risks, impls] = await Promise.all([
        controlRegisterService.getControlRisks(controlId).catch(() => []),
        controlRegisterService.getControlImplementations(controlId).catch(() => []),
      ]);
      setControlRisks(risks as ControlLinkedRisk[]);
      setControlImplementations(impls as ControlImplementationStep[]);
    } finally {
      setControlRisksLoading(false);
      setControlImplementationsLoading(false);
    }
  }, []);

  const handleSelectControl = useCallback((c: StepLinkedControl) => {
    setSelectedControl(c);
    loadControlContext(c.id);
  }, [loadControlContext]);

  const handleOpenConfig = (proc: Procedure, initialTab: ConfigTab = "configure") => {
    setActiveProcedureForConfig(proc);
    setSelectedControl(null);
    setControlRisks([]);
    setControlImplementations([]);
    // Pre-fill the Edit Step tab with this step's data
    setSelectedProc(proc);
    setProcFormData(proc);
    setProcAuthorities(
      (proc.authorities || []).map((s) => ({
        id: s.id,
        authority_id: s.authority_id,
        role: s.role as StepType,
      })),
    );
    setProcAuditLinks(
      (proc.audit_links || []).map((l) => ({
        id: l.id,
        procedure_template_id: l.procedure_template_id,
        procedure_name: l.procedure_name || "",
        procedure_type: l.procedure_type || "",
        authority_id: l.authority_id ?? null,
      })),
    );
    setShowProcDesc(false);
    setAuditSearch("");
    setIsProcEditMode(true);
    setSaving(false);
    setToggling(false);
    setEditingProcBasic(false);
    setConfigTab(initialTab);
    setAuditSearch("");
    setShowConfigModal(true);
    // Fire-and-forget — the column shows its own loading state. Control links
    // are not versioned: a draft step shows its live step's controls.
    const liveId = liveIdOf(proc);
    if (liveId) void loadStepControls(liveId);
    else setStepControls([]);
  };

  // Open the picker sub-modal and lazy-load all entity controls. We pull the
  // full active list once; the picker filters client-side over name/code.
  const handleAddControl = async () => {
    if (!activeProcedureForConfig) return;
    setShowControlPicker(true);
    setPickerSearch("");
    setPickerLoading(true);
    try {
      const all = await controlRegisterService.getAll();
      // Hide controls already linked to this step. Only approved + active
      // controls are linkable — getAll() returns every workflow state.
      const linkedIds = new Set(stepControls.map(c => c.id));
      setPickerControls((all || []).filter(c => !linkedIds.has(c.id) && c.is_active === 1 && c.workflow_state === 'approved'));
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to load controls." });
      setPickerControls([]);
    } finally {
      setPickerLoading(false);
    }
  };

  const handlePickerLink = async (control: ControlRegister) => {
    if (!activeProcedureForConfig) return;
    setPickerLinking(control.id);
    try {
      await controlRegisterService.linkControlToStep(liveIdOf(activeProcedureForConfig)!, control.id);
      setToast({ type: "success", message: `Linked "${control.control_name}" to this step.` });
      await loadStepControls(liveIdOf(activeProcedureForConfig)!);
      setPickerControls(prev => prev.filter(c => c.id !== control.id));
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to link control." });
    } finally {
      setPickerLinking(null);
    }
  };

  const handleUnlinkControl = async (control: StepLinkedControl) => {
    if (!activeProcedureForConfig) return;
    const ok = await confirmDelete({
      title: "Unlink this control from the step?",
      text: `"${control.name}" will no longer be associated with this SOP step. The control itself is not deleted.`,
      confirmText: "Yes, Unlink",
    });
    if (!ok) return;
    try {
      await controlRegisterService.unlinkControlFromStep(liveIdOf(activeProcedureForConfig)!, control.id);
      setToast({ type: "success", message: "Control unlinked." });
      if (selectedControl?.id === control.id) {
        setSelectedControl(null);
        setControlRisks([]);
        setControlImplementations([]);
      }
      await loadStepControls(liveIdOf(activeProcedureForConfig)!);
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to unlink control." });
    }
  };

  const handleRiskClick = (risk: ControlLinkedRisk) => {
    setSelectedRiskForVis(risk);
    setShowRiskVisModal(true);
  };

  const activeProcedures = procedures
    .filter((p) => p.process_id === activeProcessForProc?.id)
    .sort((a, b) => a.step_number - b.step_number);

  const totalProcRecords = activeProcedures.length;
  const paginatedProcedures = activeProcedures.slice(
    (procPage - 1) * procPageSize,
    procPage * procPageSize,
  );

  const afterStepOptions = procedures
    .filter(
      (p) =>
        p.process_id === activeProcessForProc?.id &&
        (!isProcEditMode || p.id !== selectedProc?.id),
    )
    .sort((a, b) => a.step_number - b.step_number)
    .map((p) => ({
      value: String(p.id),
      label: `Step ${p.step_number}: ${p.step_name}`,
    }));

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to SOPs." />
      </div>
    );
  }

  return (
    <>
      {/* merged: incoming + existing */}
      <div className="flex h-full overflow-hidden">
        <Toast toast={toast} onClose={() => setToast(null)} />
        {/* Single full-width card. Side-nav status menu replaced by inline
            status-filter dropdown in the toolbar below, so the table can use
            the full content width. */}
        <div className="netbody-card flex flex-col flex-1 min-w-0">
          {/* Toolbar — Show LEFT · Status + search + Create RIGHT */}
          <div className="netbody-header">
            {/* Active/Inactive — iOS-style on/off switch (on = active).
                Pinned to the table-left edge; filters + Create stay right. */}
            <StatusSwitch
              value={activeFilter}
              onChange={next => setActiveFilter(next)}
              noun="SOPs"
              testId="SEL_sop_active_filter"
            />
            <div className="flex items-center gap-2 ml-auto">
              <FormControl.SearchSelect
                value={statusFilter}
                onChange={v => setStatusFilter(String(v ?? 'ALL'))}
                className="filter-status"
                data-testid="SEL_sop_status_filter"
                options={[
                  { value: 'ALL', label: 'All Status' },
                  { value: 'draft', label: 'Draft' },
                  { value: 'submitted', label: 'Submitted' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'rejected', label: 'Rejected' },
                ]}
                noSearchIcon
              />
              <FormControl.Input
                type="text"
                placeholder="Search SOPs…"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                leadingIcon={<Search size={14} />}
                className="filter-search"
                data-testid="INP_sop_search"
              />
              {canModify && (
                <Button variant="fill" icon={Plus} onClick={handleAddNew} data-testid="BTN_sop_create">
                  Create
                </Button>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="netbody-content">
            <div className="scroll-container flex-1 overflow-hidden">
              <div className="table-list">
                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <thead>
                      <tr>
                        <th className="table-list-th" style={{ width: 34 }} aria-label="Workflow status"></th>
                        <th className="table-list-th table-list-cell--sno">
                          S.No
                        </th>
                        <th className="table-list-th">Code</th>
                        <th className="table-list-th">SOP Name</th>
                        <th className="table-list-th table-list-cell--truncate">
                          Description
                        </th>
                        <th className="table-list-th">Owner</th>
                        <th className="table-list-th">Authorities</th>
                        <th className="table-list-th">Controls</th>
                        <th className="table-list-th">Risks</th>
                        <th className="table-list-th">Ver.</th>
                        <th className="table-list-th">Effective</th>
                        <th className="table-list-th">Cycle</th>
                      </tr>
                    </thead>
                    <tbody aria-busy={loading} aria-live="polite">
                      {loading && <Skeleton.TableRows cols={['dot', '3xs', 'xs', 'md', 'md', 'sm', 'xs', 'xs', 'xs', 'xs', 'xs', 'xs']} />}
                      {!loading && filteredProcesses.length === 0 && (
                        <tr>
                          <td colSpan={13} className="table-list-cell">
                            <NoDataFound title="No SOPs Found" message='Click "Create" to add one.' />
                          </td>
                        </tr>
                      )}
                      {!loading &&
                        paginatedProcesses.map((process, idx) => (
                          <tr
                            key={process.id}
                            className={`table-list-row${process.is_active === 1 ? '' : ' is-inactive'}`}
                            onClick={(e) => handleConfigure(e, process)}
                            data-testid={`BTN_sop_row_${process.id}_open`}
                          >
                            <td className="table-list-cell" onClick={(e) => { e.stopPropagation(); setWorkflowRow(process); }} title="Workflow — view timeline & act">
                              <ApprovalDot
                                state={(process.workflow_state || 'draft') as WorkflowState}
                                onClick={() => setWorkflowRow(process)}
                                title="Workflow — view timeline & act"
                              />
                            </td>
                            <td className="table-list-cell table-list-cell--sno">
                              {(page - 1) * pageSize + idx + 1}
                            </td>
                            <td className="table-list-cell">
                              {(process as any).code
                                ? <span className="text-xs text-foreground">{(process as any).code}</span>
                                : <span className="text-muted text-xs">—</span>}
                            </td>
                            <td className="table-list-cell">
                              <span className="table-list-cell-title cell-name-lock">
                                {(process as any).workflow_state === 'approved' && (
                                  <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                )}
                                {process.process_name}
                              </span>
                            </td>
                            <td
                              className="table-list-cell table-list-cell--truncate"
                              title={process.description}
                            >
                              {process.description}
                            </td>
                            <td className="table-list-cell">
                              {process.owner_authority_id
                                ? (process.owner_authority_name
                                    || authorities.find(a => a.id === process.owner_authority_id)?.name
                                    || `#${process.owner_authority_id}`)
                                : (process.owner || "—")}
                            </td>
                            <td className="table-list-cell">
                              {((process as any).authority_count ?? 0) > 0 ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700" title={`${(process as any).authority_count} authority(ies) across steps`}>{(process as any).authority_count}</span>
                              ) : <span className="text-xs text-gray-400">0</span>}
                            </td>
                            <td className="table-list-cell">
                              {((process as any).control_count ?? 0) > 0 ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700" title={`${(process as any).control_count} control(s) implemented via this SOP`}>{(process as any).control_count}</span>
                              ) : <span className="text-xs text-gray-400">0</span>}
                            </td>
                            <td className="table-list-cell">
                              {((process as any).risk_count ?? 0) > 0 ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700" title={`${(process as any).risk_count} risk(s) — indirect, via controls`}>{(process as any).risk_count}</span>
                              ) : <span className="text-xs text-gray-400">0</span>}
                            </td>
                            <td className="table-list-cell">
                              v{process.current_version_number ?? process.version}
                              {process.draft_version_number != null && (
                                <div className="table-list-cell-sub" title="A draft of the next version is open">
                                  Draft v{process.draft_version_number}
                                  {process.draft_workflow_state === "submitted" ? " · awaiting approval"
                                    : process.draft_workflow_state === "rejected" ? " · returned" : ""}
                                </div>
                              )}
                            </td>
                            <td className="table-list-cell">
                              {formatDate(process.effective_date)}
                            </td>
                            <td className="table-list-cell">
                              {process.review_cycle_months}m
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                  recordLabel="SOPs"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/*
        SOP Detail Modal — multi-tab structure styled after the Frameworks /
        Policies modal (`app-modal-tabs` / `app-modal-tab` classes).

        Outer tabs: Live | Draft
          - Live: read-only view of the SOP currently in use.
          - Draft: editable view of the SOP record. Create / Edit / Inactive
                   actions are available here only.
        Version management is NOT yet implemented at the backend — both outer
        tabs currently render the SAME row. Once draft rows are persisted as a
        separate version, only the data source swaps; this layout stands.

        Inner tabs (per outer tab):
          1. Basic Details
          2. Audit Enablement
          3. Steps  (with click-to-expand drill-down)
          4. Stakeholders

        Create mode (`!isEditMode`) skips the Live/Draft outer pair — there is
        nothing "live" yet — and goes straight into the inner tabs in editable
        mode.
      */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="xl">
        <Modal.Header>
          {isEditMode && selectedProcess ? (
            <>
              {draftMode
                ? `Draft v${sopDraft!.target_version_number} of `
                : sopIsApproved ? `Viewing live v${selectedProcess.current_version_number ?? 1} of `
                : (editingBasic || editingAudit || editingStakeholders) ? "Editing " : "Viewing "}
              "<span className="item-highlight">{selectedProcess.process_name}</span>"
            </>
          ) : (
            "Create SOP"
          )}
        </Modal.Header>
        <Modal.Body className="p-0 flex flex-col">
          {/* Tabs sticky-glued to the top of the modal body so they stay in
              view while the per-tab panel scrolls independently. Without
              this, tall panels (Steps timeline, drill-down etc.) pushed the
              tab strip off-screen and the user had to scroll back up. */}
          <div
            className="app-modal-tabs"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              background: "#fff",
              marginBottom: 0,
              paddingLeft: 16,
              paddingRight: 16,
              flexShrink: 0,
            }}
          >
            {/* Stakeholders moved to the end — it is a *derived* roll-up
                of the Live + Draft step authorities, so it sits to the right
                of the data it depends on. */}
            {([
              { key: "basic",        label: "Basic Details" },
              { key: "draft",        label: "Draft SOP" },
              { key: "live",         label: "Live SOP" },
              { key: "versions",     label: "Versions" },
              { key: "stakeholders", label: "Stakeholders" },
              { key: "audit",        label: "Audit Enablement" },
              { key: "cadence",      label: "Cadence" },
              { key: "programs",     label: "Rolled-out Programs" },
            ] as const).map(t => {
              // Every tab after Basic requires an existing record.
              const disabled = !isEditMode && t.key !== "basic";
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`TAB_sop_${t.key}`}
                  className={`app-modal-tab${sopTab === t.key ? " app-modal-tab--active" : ""}`}
                  onClick={() => !disabled && setSopTab(t.key)}
                  disabled={disabled}
                  style={disabled ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Per-tab panel — owns its own vertical scroll so tab bar stays put. */}
          <div className="p-4" style={{ flex: "1 1 auto", overflowY: "auto", minHeight: 0 }}>
            {/* ── Basic Details ─────────────────────────────────────────── */}
            {sopTab === "basic" && (() => {
              // Create mode is always editable; in edit mode the section is
              // read-only until the user clicks Edit in the modal footer.
              // Approved records stay locked regardless.
              const editing = basicEditable && (!isEditMode || editingBasic);
              return (
                <>
                  {draftMode ? (
                    <div className="approved-banner col-span-2" style={{ marginBottom: 16 }} data-testid="BNR_sop_draft_basic">
                      Showing the draft of version {sopDraft!.target_version_number}. Live version{" "}
                      {sopDraft!.target_version_number - 1} stays in use until this draft is approved.
                      {!draftEditable && " The draft is awaiting approval and cannot be changed."}
                    </div>
                  ) : sopIsApproved && (
                    <div className="approved-banner col-span-2" style={{ marginBottom: 16 }}>
                      <Lock size={14} /> This SOP is live (version {selectedProcess?.current_version_number ?? 1}) and locked.
                      To change it, start a new version on the Draft SOP tab.
                    </div>
                  )}
                  <form
                    id="sop-form"
                    ref={sopFormRef}
                    onSubmit={handleSubmit}
                    className="grid grid-cols-2 gap-x-6 gap-y-4"
                  >
                    {isEditMode && (
                      <FormControl.Input
                        label="Code"
                        className="col-span-2"
                        value={(formData as any).code || (selectedProcess as any)?.code || (formData as any).process_code || "—"}
                        disabled
                        helperText="Auto-generated"
                      />
                    )}
                    <FormControl.Input
                      label="SOP Name"
                      required={editing}
                      className="col-span-2"
                      value={formData.process_name || ""}
                      disabled={!editing}
                      onChange={(e) => setFormData({ ...formData, process_name: e.target.value })}
                      placeholder="e.g., Vendor Onboarding Process"
                      data-testid="INP_sop_name"
                    />
                    <FormControl.Textarea
                      label="Description"
                      className="col-span-2"
                      value={formData.description || ""}
                      disabled={!editing}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      data-testid="INP_sop_description"
                    />
                    <FormControl.SearchSelect
                      label="Owner"
                      className="col-span-2"
                      options={authorities.map(a => ({ value: String(a.id), label: a.name }))}
                      value={(formData as any).owner_authority_id ? String((formData as any).owner_authority_id) : ""}
                      disabled={!editing}
                      onChange={(value) => setFormData({ ...formData, owner_authority_id: value ? Number(value) : null } as any)}
                      placeholder="Select authority"
                      data-testid="SEL_sop_owner"
                    />
                    <div className="col-span-2 h-px bg-gray-100"></div>
                    {isEditMode && (
                      <>
                        <FormControl.Input
                          label="Version"
                          value={draftMode
                            ? `${sopDraft!.target_version_number} (draft)`
                            : String(selectedProcess?.current_version_number ?? formData.version ?? "1")}
                          disabled
                          helperText="Set automatically when a version is approved"
                          data-testid="INP_sop_version"
                        />
                        <FormControl.SearchSelect
                          label="Status"
                          options={SOP_STATUS_OPTIONS}
                          value={formData.process_status || "DRAFT"}
                          disabled={!editing || draftMode}
                          onChange={(value) => setFormData({ ...formData, process_status: value as any })}
                          data-testid="SEL_sop_status"
                        />
                      </>
                    )}
                    <FormControl.DatePicker
                      label="Effective Date"
                      value={formData.effective_date || ""}
                      disabled={!editing}
                      onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                      data-testid="INP_sop_effective_date"
                    />
                    <FormControl.Input
                      label="Review Cycle (Months)"
                      type="number"
                      value={formData.review_cycle_months || 12}
                      disabled={!editing}
                      onChange={(e) =>
                        setFormData({ ...formData, review_cycle_months: parseInt(e.target.value) })
                      }
                    />
                  </form>
                </>
              );
            })()}

            {/* ── Audit Enablement ──────────────────────────────────────── */}
            {sopTab === "audit" && (() => {
              const editing = !sopIsApproved && (!isEditMode || editingAudit);
              return (
                <div className="flex flex-col gap-y-2 max-w-2xl">
                  {sopIsApproved && (
                    <div className="approved-banner" style={{ marginBottom: 8 }}>
                      <Lock size={14} /> This SOP is live and locked.
                    </div>
                  )}
                  <div className="text-sm font-semibold text-gray-800">Audit procedure visibility</div>
                  <div className="text-xs text-gray-500 mb-2">
                    Enable which audit checks can be run against this SOP. Toggling off does not
                    affect already-running audits — only stops new audits from picking the procedure.
                  </div>
                  <FormControl.Checkbox
                    label="Enable Test of Design (Process Walkthrough — Design)"
                    checked={!!(formData as any).enable_test_of_design}
                    disabled={!editing}
                    onChange={(checked) =>
                      setFormData({ ...(formData as any), enable_test_of_design: checked })
                    }
                  />
                  <FormControl.Checkbox
                    label="Enable Test of Compliance (Process Walkthrough — Compliance)"
                    checked={!!(formData as any).enable_test_of_compliance}
                    disabled={!editing}
                    onChange={(checked) =>
                      setFormData({ ...(formData as any), enable_test_of_compliance: checked })
                    }
                  />
                </div>
              );
            })()}

            {/* ── Stakeholders ──────────────────────────────────────────── */}
            {sopTab === "stakeholders" && (
              !isEditMode ? (
                <div className="text-xs text-gray-500 italic p-4 border border-dashed border-gray-200 rounded bg-gray-50">
                  Save the SOP first — stakeholders are derived from its steps.
                </div>
              ) : (
                // Always read-only: derived view, no edit toggle.
                <SopStakeholdersPanel processId={selectedProcess!.id} readOnly />
              )
            )}

            {/* ── Live SOP ──────────────────────────────────────────────── */}
            {sopTab === "live" && (
              !isEditMode ? null : (
                <SopStepsPanel
                  processId={selectedProcess!.id}
                  sopCode={selectedProcess?.process_code}
                  readOnly={true}
                  onStepClick={async (s) => {
                    setActiveProcessForProc(selectedProcess);
                    if (selectedProcess) await loadSteps(selectedProcess.id);
                    handleOpenConfig(s as any, "configure");
                  }}
                />
              )
            )}

            {/* ── Draft SOP ─────────────────────────────────────────────── */}
            {/* Three cases:
                 1. SOP not live yet  → edit its steps in place; send v1 for approval.
                 2. Live, no draft    → offer to start the next version.
                 3. Live, draft open  → edit the draft's steps; its own approval
                    workflow; approving it makes it the live version. */}
            {sopTab === "draft" && (
              !isEditMode || !selectedProcess ? null : draftLoading ? (
                <div className="p-6 text-sm text-gray-400">Loading draft…</div>
              ) : !sopIsApproved ? (
                <div className="flex flex-col gap-4">
                  <SopStepsPanel
                    ref={draftStepsPanelRef}
                    processId={selectedProcess.id}
                    sopCode={selectedProcess.process_code}
                    readOnly={(selectedProcess as any).workflow_state === "submitted"}
                    readOnlyNote="Sent for approval — steps cannot be changed until it is approved or returned."
                    headerAction={
                      <Button
                        variant="outline"
                        icon={ArrowRight}
                        onClick={() => setWorkflowRow(selectedProcess)}
                        data-testid="BTN_sop_send_v1_for_approval"
                      >
                        {(selectedProcess as any).workflow_state === "submitted"
                          ? "Approval workflow"
                          : "Send version 1 for approval"}
                      </Button>
                    }
                    onCreateStep={async () => {
                      // The legacy `handleAddProc` reads from `activeProcessForProc`
                      // AND the `procedures` state (for predecessor options). Both
                      // must be primed before the modal opens, otherwise the
                      // "Depends on previous step" radio has nothing to pick.
                      setActiveProcessForProc(selectedProcess);
                      await loadSteps(selectedProcess.id);
                      handleAddProc();
                    }}
                    onStepClick={async (s) => {
                      setActiveProcessForProc(selectedProcess);
                      await loadSteps(selectedProcess.id);
                      handleOpenConfig(s as any, "configure");
                    }}
                    onEditStep={async (s) => {
                      setActiveProcessForProc(selectedProcess);
                      await loadSteps(selectedProcess.id);
                      handleOpenConfig(s as any, "edit");
                    }}
                  />
                </div>
              ) : !sopDraft ? (
                <div className="flex flex-col gap-3 max-w-2xl" data-testid="PNL_sop_start_version">
                  <div className="approved-banner">
                    <Lock size={14} /> Version {selectedProcess.current_version_number ?? 1} is live and locked.
                  </div>
                  <p className="text-sm text-gray-700">
                    To change this SOP, start version {(selectedProcess.current_version_number ?? 1) + 1}. It begins as a
                    copy of the live version. You edit the copy, send it for approval, and when it is approved it
                    replaces the live version. Until then the live version stays in use, and runs already
                    started keep the version they started with.
                  </p>
                  {canModify && (
                    <>
                      <FormControl.Textarea
                        label="Reason for the new version (optional)"
                        value={newVersionReason}
                        onChange={(e) => setNewVersionReason(e.target.value)}
                        rows={2}
                        placeholder="e.g. Add a manager approval step after the 2026 audit finding"
                        data-testid="INP_sop_new_version_reason"
                      />
                      <div>
                        <Button
                          variant="fill"
                          icon={Plus}
                          disabled={saving}
                          data-testid="BTN_sop_start_new_version"
                          onClick={async () => {
                            setSaving(true);
                            try {
                              const d = await sopVersioningService.openDraft(selectedProcess.id, newVersionReason.trim());
                              await loadDraft(selectedProcess.id);
                              setNewVersionReason("");
                              setToast({ type: "success", message: `Draft of version ${d.target_version_number} started.` });
                              await loadProcesses();
                            } catch (e: any) {
                              setToast({ type: "error", message: e?.response?.data?.detail || e.message || "Could not start a new version." });
                            } finally {
                              setSaving(false);
                            }
                          }}
                        >
                          {saving ? "Starting…" : `Start version ${(selectedProcess.current_version_number ?? 1) + 1}`}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-4" data-testid="PNL_sop_draft">
                  <div className="flex items-start gap-3 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                    <span
                      onClick={() => setDraftWorkflowOpen(true)}
                      title="Workflow — view timeline & act"
                      style={{ display: "inline-flex", cursor: "pointer", paddingTop: 2 }}
                    >
                      <ApprovalDot
                        state={sopDraft.workflow_state as WorkflowState}
                        onClick={() => setDraftWorkflowOpen(true)}
                        title="Workflow — view timeline & act"
                      />
                    </span>
                    <div className="flex-1 text-sm">
                      <div className="font-semibold text-gray-800">
                        Draft of version {sopDraft.target_version_number}
                        <span className="text-gray-500 font-normal">
                          {" "}· {sopDraft.workflow_state === "submitted" ? "awaiting approval"
                            : sopDraft.workflow_state === "rejected" ? "returned for changes" : "being edited"}
                        </span>
                      </div>
                      {sopDraft.change_summary && (
                        <div className="text-xs text-gray-600">Reason: {sopDraft.change_summary}</div>
                      )}
                      {sopDraft.workflow_state === "rejected" && sopDraft.rejection_reason && (
                        <div className="text-xs text-red-700">Returned: {sopDraft.rejection_reason}</div>
                      )}
                      <div className="text-xs text-gray-500">
                        Live version {sopDraft.target_version_number - 1} stays in use until this draft is approved.
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            setDraftChanges(draftChanges ? null : await sopVersioningService.getDraftChanges(sopDraft.id));
                          } catch (e: any) {
                            setToast({ type: "error", message: e?.response?.data?.detail || "Could not load changes." });
                          }
                        }}
                        data-testid="BTN_sop_draft_changes"
                      >
                        {draftChanges ? "Hide changes" : "What changed"}
                      </Button>
                      <Button
                        variant={draftEditable ? "fill" : "outline"}
                        icon={ArrowRight}
                        onClick={() => setDraftWorkflowOpen(true)}
                        data-testid="BTN_sop_draft_workflow"
                      >
                        {draftEditable ? "Send for approval" : "Approval workflow"}
                      </Button>
                      {draftEditable && canModify && (
                        <Button
                          variant="inactive-outline"
                          icon={X}
                          disabled={saving}
                          data-testid="BTN_sop_draft_discard"
                          onClick={async () => {
                            const ok = await confirmWarning({
                              title: `Discard the draft of version ${sopDraft.target_version_number}?`,
                              text: "All changes in this draft are lost. The live version is not affected.",
                              confirmText: "Yes, Discard",
                            });
                            if (!ok) return;
                            setSaving(true);
                            try {
                              await sopVersioningService.discardDraft(sopDraft.id);
                              setToast({ type: "warning", message: "Draft discarded." });
                              await refreshSelectedProcess();
                            } catch (e: any) {
                              setToast({ type: "error", message: e?.response?.data?.detail || "Could not discard the draft." });
                            } finally {
                              setSaving(false);
                            }
                          }}
                        >
                          Discard draft
                        </Button>
                      )}
                    </div>
                  </div>
                  {draftChanges && (
                    <div data-testid="PNL_sop_draft_changes">
                      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                        Changes from live version {sopDraft.target_version_number - 1}
                      </div>
                      <SopChangesList changes={draftChanges} emptyText="Nothing changed yet." />
                    </div>
                  )}
                  <SopStepsPanel
                    key={sopDraft.id}
                    ref={draftStepsPanelRef}
                    processId={selectedProcess.id}
                    draftId={sopDraft.id}
                    sopCode={selectedProcess.process_code}
                    readOnly={!draftEditable}
                    readOnlyNote="Awaiting approval — the draft cannot be changed unless the approver returns it."
                    onCreateStep={draftEditable ? async () => {
                      setActiveProcessForProc(selectedProcess);
                      await loadSteps(selectedProcess.id);
                      handleAddProc();
                    } : undefined}
                    onStepClick={async (s) => {
                      setActiveProcessForProc(selectedProcess);
                      await loadSteps(selectedProcess.id);
                      handleOpenConfig(s as any, "configure");
                    }}
                    onEditStep={draftEditable ? async (s) => {
                      setActiveProcessForProc(selectedProcess);
                      await loadSteps(selectedProcess.id);
                      handleOpenConfig(s as any, "edit");
                    } : undefined}
                  />
                </div>
              )
            )}

            {/* ── Versions ──────────────────────────────────────────────── */}
            {sopTab === "versions" && isEditMode && selectedProcess && (
              <SopVersionsPanel processId={selectedProcess.id} refreshKey={versionsKey} />
            )}

            {/* ── Cadence (migration 114) ──────────────────────────────── */}
            {sopTab === "cadence" && isEditMode && selectedProcess && (
              <TemplateCadenceTab
                sourceTypeId={SOP_TYPE_ID}
                sourceRecordId={selectedProcess.id}
                sourceTypeCode={SOP_TYPE_CODE}
                sourceName={selectedProcess.process_name || `SOP #${selectedProcess.id}`}
                sourceDisplayName={SOP_DISPLAY}
              />
            )}

            {/* ── Rolled-out Programs (migration 114) ──────────────────── */}
            {sopTab === "programs" && isEditMode && selectedProcess && (
              <TemplateProgramsTab
                sourceTypeId={SOP_TYPE_ID}
                sourceRecordId={selectedProcess.id}
                sourceTypeCode={SOP_TYPE_CODE}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className={isEditMode && selectedProcess && sopTab === "basic" ? "app-modal-footer--split" : ""}>
          {/* LEFT: Inactive/Active toggle — basic tab, edit mode only.
              Hidden once approved+locked (approver acts via the workflow dot). */}
          {isEditMode && selectedProcess && sopTab === "basic" && !sopIsApproved && (
            <Button
              variant={selectedProcess.is_active === 1 ? "inactive-outline" : "active-outline"}
              icon={selectedProcess.is_active === 1 ? CircleMinus : CircleCheck}
              onClick={handleToggleActive}
              disabled={toggling || saving}
              data-testid="BTN_sop_toggle_active"
            >
              {toggling ? "..." : selectedProcess.is_active === 1 ? "Inactive" : "Active"}
            </Button>
          )}
          <div className="flex gap-2">
            {/* Create mode: Submit */}
            {!isEditMode && sopTab === "basic" && (
              <Button
                type="submit"
                form="sop-form"
                variant="fill"
                icon={Save}
                disabled={saving || toggling}
                data-testid="BTN_sop_submit"
              >
                {saving ? "Saving…" : "Save & Continue to Draft SOP"}
              </Button>
            )}
            {/* Edit mode — basic tab: Edit ↔ Update (ref-based submit).
                Hidden once approved+locked. */}
            {isEditMode && selectedProcess && sopTab === "basic" && basicEditable && (
              editingBasic ? (
                <Button
                  variant="update"
                  icon={Check}
                  onClick={() => sopFormRef.current?.requestSubmit()}
                  disabled={saving || toggling}
                  data-testid="BTN_sop_update"
                >
                  {saving ? "Updating..." : "Update"}
                </Button>
              ) : (
                <Button
                  variant="fill"
                  icon={Edit2}
                  onClick={() => setEditingBasic(true)}
                  disabled={saving || toggling}
                  data-testid="BTN_sop_edit"
                >
                  Edit
                </Button>
              )
            )}
            {/* Edit mode — audit tab: Edit ↔ Update (direct call — no form element on this tab).
                Hidden once approved+locked. */}
            {isEditMode && selectedProcess && sopTab === "audit" && !sopIsApproved && (
              editingAudit ? (
                <Button
                  variant="update"
                  icon={Check}
                  onClick={() => handleSubmit({ preventDefault: () => { } } as any)}
                  disabled={saving || toggling}
                  data-testid="BTN_sop_audit_update"
                >
                  {saving ? "Updating..." : "Update"}
                </Button>
              ) : (
                <Button
                  variant="fill"
                  icon={Edit2}
                  onClick={() => setEditingAudit(true)}
                  disabled={saving || toggling}
                  data-testid="BTN_sop_audit_edit"
                >
                  Edit
                </Button>
              )
            )}
          </div>
        </Modal.Footer>
      </Modal>

      {/* Procedures List Modal */}
      <Modal
        isOpen={showProcListModal}
        onClose={() => setShowProcListModal(false)}
        size="xl"
      >
        <Modal.Header
          titleAction={
            activeProcessForProc && (
              <button
                className="btn-primary-outline-none app-modal-header-icon"
                onClick={() => {
                  setSelectedProcess(activeProcessForProc);
                  setFormData(activeProcessForProc);
                  setIsEditMode(true);
                  setSaving(false);
                  setToggling(false);
                  setSopTab("basic");
                  setEditingBasic(true);
                  setEditingAudit(false);
                  setEditingStakeholders(false);
                  setShowModal(true);
                }}
                title="Edit SOP"
              >
                <Edit2 size={16} />
              </button>
            )
          }
        >
          {activeProcessForProc ? (
            <>
              Procedures for "
              <span className="item-highlight">
                {activeProcessForProc.process_name}
              </span>
              "
            </>
          ) : (
            "Manage Procedures"
          )}
        </Modal.Header>
        <Modal.Body>
          {/* Toolbar */}
          <div className="netbody-header">
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="fill" icon={Plus} onClick={handleAddProc}>
                Create
              </Button>
            </div>
          </div>

          <div className="table-list" style={{ marginTop: "8px" }}>
            {!stepsLoading && activeProcedures.length === 0 ? (
              <NoDataFound title="No Procedure Steps Yet" message='Click "Create" to add one.' />
            ) : (
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Step #</th>
                    <th className="table-list-th">Name</th>
                    <th className="table-list-th">Type</th>
                    <th className="table-list-th">Authorities</th>
                    <th className="table-list-th">After</th>
                    <th className="table-list-th">Timeline</th>
                  </tr>
                </thead>
                <tbody aria-busy={stepsLoading} aria-live="polite">
                  {stepsLoading && <Skeleton.TableRows cols={['3xs', 'xs', 'md', 'sm', 'sm', 'xs', 'xs']} />}
                  {!stepsLoading &&
                    paginatedProcedures.map((proc, idx) => {
                      const anchorLabel =
                        proc.timeline_anchor === "AFTER_PREV_START"
                          ? "after start of prev"
                          : proc.timeline_anchor === "FIXED"
                            ? "fixed"
                            : "after end of prev";
                      return (
                        <tr
                          key={proc.id}
                          className="table-list-row"
                          onClick={() => handleOpenConfig(proc)}
                        >
                          <td className="table-list-cell table-list-cell--sno">
                            {(procPage - 1) * procPageSize + idx + 1}
                          </td>
                          <td className="table-list-cell">
                            {proc.step_number}
                          </td>
                          <td className="table-list-cell">
                            <span className="table-list-cell-title">
                              {proc.step_name}
                            </span>
                            {proc.description && (
                              <span className="table-list-cell-sub">
                                {proc.description}
                              </span>
                            )}
                          </td>
                          <td className="table-list-cell">
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                              {proc.step_type || "EXECUTE"}
                            </span>
                          </td>
                          <td className="table-list-cell">
                            <div className="flex flex-wrap gap-1">
                              {proc.authorities &&
                                proc.authorities.length > 0 ? (
                                proc.authorities.map((sh) => (
                                  <span
                                    key={sh.id}
                                    title={sh.role}
                                    className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 whitespace-nowrap"
                                  >
                                    {sh.authority_name}
                                    <span className="text-gray-400 ml-1">
                                      ({sh.role[0]})
                                    </span>
                                  </span>
                                ))
                              ) : (
                                <span className="text-gray-400 text-xs">—</span>
                              )}
                            </div>
                          </td>
                          <td className="table-list-cell">
                            {proc.after_step_name || (
                              <span className="text-gray-400">first step</span>
                            )}
                          </td>
                          <td className="table-list-cell">
                            {proc.timeline_days != null ? (
                              `${proc.timeline_days}d ${anchorLabel}`
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            )}
            <Pagination
              page={procPage}
              pageSize={procPageSize}
              totalRecords={totalProcRecords}
              onPageChange={setProcPage}
              onPageSizeChange={setProcPageSize}
              recordLabel="steps"
            />
          </div>
        </Modal.Body>
      </Modal>

      {/* Procedure Step Create / Edit Modal */}
      <Modal
        isOpen={showProcFormModal}
        onClose={() => setShowProcFormModal(false)}
        size="md"
      >
        <Modal.Header>
          {isProcEditMode && selectedProc ? (
            <>
              Editing "
              <span className="item-highlight">{selectedProc.step_name}</span>"
            </>
          ) : (
            "Create Procedure Step"
          )}
        </Modal.Header>
        <Modal.Body>
          <form id="proc-step-form" onSubmit={handleProcSubmit} className="flex flex-col gap-0">

            {/* ── 1. What to do ─────────────────────────────────────────── */}
            <div className="px-1 pb-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">1</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">What to do</span>
              </div>
              <div className="relative">
                <FormControl.Input
                  label="Step name"
                  required
                  maxLength={100}
                  value={(procFormData as any).step_name || ""}
                  onChange={(e) => setProcFormData({ ...procFormData, step_name: e.target.value } as any)}
                  placeholder="e.g. Verify employment documents"
                  data-testid="INP_sop_step_name"
                />
                <span className={`absolute right-2 bottom-2 text-[10px] tabular-nums ${((procFormData as any).step_name || "").length > 90 ? "text-amber-500" : "text-gray-300"}`}>
                  {((procFormData as any).step_name || "").length}/100
                </span>
              </div>
              <div className="mt-2">
                <button
                  type="button"
                  className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 transition"
                  onClick={() => setShowProcDesc((v) => !v)}
                >
                  <ChevronDown size={12} className={`transition-transform ${showProcDesc ? "rotate-180" : ""}`} />
                  {showProcDesc ? "Hide" : "Add"} detailed instructions
                </button>
                {showProcDesc && (
                  <div className="mt-2">
                    <FormControl.Textarea
                      label=""
                      value={procFormData.description || ""}
                      onChange={(e) => setProcFormData({ ...procFormData, description: e.target.value })}
                      rows={3}
                      placeholder="Describe the action, required inputs, and expected output."
                    />
                  </div>
                )}
              </div>
            </div>

            {/* ── 2. Who does it ────────────────────────────────────────── */}
            <div className="border-t border-gray-100 px-1 py-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">2</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Who does it</span>
                <button
                  type="button"
                  className="ml-auto text-xs text-emerald-700 hover:text-emerald-900 font-medium flex items-center gap-1"
                  onClick={addAuthorityRow}
                >
                  <Plus size={12} /> Add person / team
                </button>
              </div>
              {procAuthorities.length === 0 ? (
                <button
                  type="button"
                  onClick={addAuthorityRow}
                  className="w-full border border-dashed border-gray-200 rounded-lg py-4 text-xs text-gray-400 hover:border-emerald-300 hover:text-emerald-600 transition flex flex-col items-center gap-1"
                >
                  <Plus size={16} />
                  <span>Add a person or team responsible for this step</span>
                </button>
              ) : (
                <div className="flex flex-col gap-2">
                  {procAuthorities.map((row, idx) => (
                    <div key={idx} className="flex gap-2 items-center bg-gray-50 rounded-lg px-3 py-2">
                      <div className="flex-1">
                        <FormControl.SearchSelect
                          label=""
                          options={authorities.map((a) => ({ value: String(a.id), label: a.name }))}
                          value={row.authority_id != null ? String(row.authority_id) : ""}
                          onChange={(v) => updateAuthorityRow(idx, { authority_id: v ? parseInt(String(v)) : null })}
                          clearable
                          placeholder="Who?"
                        />
                      </div>
                      <div className="w-40">
                        <FormControl.SearchSelect
                          label=""
                          options={ROLE_OPTIONS}
                          value={row.role}
                          onChange={(v) => updateAuthorityRow(idx, { role: (v as StepType) || "PERFORM" })}
                        />
                      </div>
                      <button
                        type="button"
                        className="text-gray-300 hover:text-red-400 flex-shrink-0 transition"
                        title="Remove"
                        onClick={() => removeAuthorityRow(idx)}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── 3. When ───────────────────────────────────────────────── */}
            <div className="border-t border-gray-100 px-1 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">3</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">When</span>
              </div>
              <div className="flex gap-3">
                {/* Independent card */}
                <button
                  type="button"
                  onClick={() => setProcFormData({ ...procFormData, after_step_id: null, timeline_days: null } as any)}
                  className={`flex-1 rounded-lg border-2 px-4 py-3 text-left transition ${(procFormData as any).after_step_id == null ? "border-emerald-400 bg-emerald-50" : "border-gray-200 bg-white hover:border-gray-300"}`}
                >
                  <div className="text-sm font-semibold text-gray-800">Can start any time</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">No dependency on other steps</div>
                </button>
                {/* Depends card */}
                <button
                  type="button"
                  disabled={afterStepOptions.length === 0}
                  onClick={() => {
                    if (afterStepOptions.length === 0) return;
                    const first = afterStepOptions[0];
                    setProcFormData({
                      ...procFormData,
                      after_step_id: (procFormData as any).after_step_id ?? parseInt(String(first.value)),
                      timeline_anchor: (procFormData as any).timeline_anchor || "AFTER_PREV_END",
                    } as any);
                  }}
                  className={`flex-1 rounded-lg border-2 px-4 py-3 text-left transition ${(procFormData as any).after_step_id != null ? "border-purple-400 bg-purple-50" : afterStepOptions.length === 0 ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed" : "border-gray-200 bg-white hover:border-gray-300"}`}
                >
                  <div className="text-sm font-semibold text-gray-800">Follows another step</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">
                    {afterStepOptions.length === 0 ? "Add earlier steps first" : "Starts after a predecessor completes"}
                  </div>
                </button>
              </div>

              {(procFormData as any).after_step_id != null && (
                <div className="grid grid-cols-3 gap-x-4 gap-y-3 mt-3">
                  <FormControl.SearchSelect
                    label="After step"
                    options={afterStepOptions}
                    value={String((procFormData as any).after_step_id)}
                    onChange={(value) => setProcFormData({ ...procFormData, after_step_id: value ? parseInt(String(value)) : null } as any)}
                    clearable
                    placeholder="Choose predecessor"
                  />
                  <FormControl.Input
                    label="Days offset"
                    type="number"
                    value={(procFormData as any).timeline_days ?? ""}
                    onChange={(e) => setProcFormData({ ...procFormData, timeline_days: e.target.value === "" ? null : parseInt(e.target.value) } as any)}
                    placeholder="e.g. 3"
                  />
                  <FormControl.SearchSelect
                    label="Counted from"
                    options={TIMELINE_ANCHORS}
                    value={(procFormData as any).timeline_anchor || "AFTER_PREV_END"}
                    onChange={(value) => setProcFormData({ ...procFormData, timeline_anchor: value } as any)}
                  />
                </div>
              )}
            </div>

            {/* ── 4. How — linked audit procedures ─────────────────────── */}
            <div className="border-t border-gray-100 px-1 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">4</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">How — audit procedures</span>
              </div>
              {draftRef.current && !(selectedProc && liveIdOf(selectedProc)) && (
                <p className="text-xs text-amber-700 mb-2">
                  Audit procedures can be linked to this step once the new version is live.
                </p>
              )}
              {/* Search box */}
              {!draftRef.current && (
              <div className="relative mb-2">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-blue-300"
                  placeholder="Search audit procedures…"
                  value={auditSearch}
                  onChange={(e) => setAuditSearch(e.target.value)}
                />
              </div>
              )}
              {/* Search results dropdown */}
              {auditSearch.trim().length > 0 && (
                <div className="border border-gray-200 rounded-lg bg-white shadow-sm mb-2 max-h-40 overflow-y-auto">
                  {auditSearchLoading ? (
                    <p className="text-xs text-gray-400 italic px-3 py-2">Searching…</p>
                  ) : auditSearchResults.filter((t) => !procAuditLinks.some((l) => l.procedure_template_id === t.id)).length === 0 ? (
                    <p className="text-xs text-gray-400 italic px-3 py-2">No matching procedures.</p>
                  ) : (
                    auditSearchResults
                      .filter((t) => !procAuditLinks.some((l) => l.procedure_template_id === t.id))
                      .map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 flex items-center justify-between gap-2 border-b border-gray-50 last:border-0"
                          onClick={() => addAuditLink(t)}
                        >
                          <span className="text-gray-800 flex-1 truncate">{t.procedure_name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 whitespace-nowrap flex-shrink-0">
                            {PROCEDURE_TYPE_LABEL[t.procedure_type] || t.procedure_type}
                          </span>
                        </button>
                      ))
                  )}
                </div>
              )}
              {/* Selected links */}
              {procAuditLinks.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No audit procedures linked yet.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {procAuditLinks.map((link, idx) => (
                    <div key={idx} className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 mb-1.5">
                        <FileText size={12} className="text-blue-400 flex-shrink-0" />
                        <span className="text-xs text-gray-800 flex-1 truncate font-medium">{link.procedure_name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-blue-100 text-blue-600 whitespace-nowrap flex-shrink-0">
                          {PROCEDURE_TYPE_LABEL[link.procedure_type] || link.procedure_type}
                        </span>
                        <button type="button" className="text-gray-300 hover:text-red-400 flex-shrink-0 transition" onClick={() => removeAuditLink(idx)}>
                          <X size={12} />
                        </button>
                      </div>
                      <div className="pl-4">
                        <FormControl.SearchSelect
                          label=""
                          options={[{ value: "", label: "— No authority assigned —" }, ...authorities.map((a) => ({ value: String(a.id), label: a.name }))]}
                          value={link.authority_id != null ? String(link.authority_id) : ""}
                          onChange={(v) => updateAuditLink(idx, { authority_id: v ? Number(v) : null })}
                          placeholder="Authority who runs this procedure…"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </form>
        </Modal.Body>
        <Modal.Footer
          className={
            isProcEditMode && selectedProc ? "app-modal-footer--split" : ""
          }
        >
          {isProcEditMode && selectedProc && (
            <Button
              variant={
                selectedProc.is_active === 1
                  ? "inactive-outline"
                  : "active-outline"
              }
              icon={selectedProc.is_active === 1 ? CircleMinus : CircleCheck}
              onClick={handleToggleStepActive}
              disabled={toggling || saving}
            >
              {toggling
                ? "..."
                : selectedProc.is_active === 1
                  ? "Inactive"
                  : "Active"}
            </Button>
          )}
          <Button
            type="submit"
            form="proc-step-form"
            variant={isProcEditMode ? "update" : "fill"}
            icon={isProcEditMode ? Check : Save}
            disabled={saving || toggling}
            data-testid="BTN_sop_step_submit"
          >
            {saving
              ? isProcEditMode
                ? "Updating..."
                : "Submitting..."
              : isProcEditMode
                ? "Update"
                : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Procedure Configuration Modal — tabs: Edit Step | Configuration */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        size="xl"
        closeOnOverlayClick={false}
      >
        <Modal.Header>
          {activeProcedureForConfig ? (
            <div className="flex items-center gap-2">
              <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded border border-blue-200">
                Step {activeProcedureForConfig.step_number}
              </span>
              <span className="text-gray-700">
                {activeProcedureForConfig.step_name}
              </span>
            </div>
          ) : (
            "Procedure Configuration"
          )}
        </Modal.Header>

        <Modal.Body className="">
          {/* Tab bar */}
          <div
            className="app-modal-tabs"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              background: "#fff",
              paddingLeft: 16,
              paddingRight: 16,
              flexShrink: 0,
            }}
          >
            {([{ key: "edit", label: "Basic Details" }, { key: "configure", label: "Configuration" }] as const).map((t) => (
              <button
                key={t.key}
                type="button"
                className={`app-modal-tab${configTab === t.key ? " app-modal-tab--active" : ""}`}
                onClick={() => setConfigTab(t.key)}
                data-testid={`TAB_sop_step_${t.key === "edit" ? "basic" : "configure"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ── Edit Step tab ─────────────────────────────────────────── */}
          {configTab === "edit" && (
            <div className="p-4">
              <form
                id="config-proc-step-form"
                ref={procFormRef}
                onSubmit={handleProcSubmit}
                className="flex flex-col gap-0"
              >
                {/* 1. What to do */}
                <div className="pb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">1</span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">What to do</span>
                  </div>
                  <div className="relative">
                    <FormControl.Input
                      label="Step name"
                      required
                      maxLength={100}
                      value={(procFormData as any).step_name || ""}
                      disabled={!editingProcBasic}
                      onChange={(e) => setProcFormData({ ...procFormData, step_name: e.target.value } as any)}
                      placeholder="e.g. Verify employment documents"
                    />
                    {editingProcBasic && (
                      <span className={`absolute right-2 bottom-2 text-[10px] tabular-nums ${((procFormData as any).step_name || "").length > 90 ? "text-amber-500" : "text-gray-300"}`}>
                        {((procFormData as any).step_name || "").length}/100
                      </span>
                    )}
                  </div>
                  <div className="mt-2">
                    <button
                      type="button"
                      className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 transition"
                      onClick={() => setShowProcDesc((v) => !v)}
                    >
                      <ChevronDown size={12} className={`transition-transform ${showProcDesc ? "rotate-180" : ""}`} />
                      {showProcDesc ? "Hide" : (procFormData.description ? "Show" : "Add")} detailed instructions
                    </button>
                    {showProcDesc && (
                      <div className="mt-2">
                        <FormControl.Textarea
                          label=""
                          value={procFormData.description || ""}
                          disabled={!editingProcBasic}
                          onChange={(e) => setProcFormData({ ...procFormData, description: e.target.value })}
                          rows={3}
                          placeholder="Describe the action, required inputs, and expected output."
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* 2. Who does it */}
                <div className="border-t border-gray-100 py-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">2</span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Who does it</span>
                    {editingProcBasic && (
                      <button
                        type="button"
                        className="ml-auto text-xs text-emerald-700 hover:text-emerald-900 font-medium flex items-center gap-1"
                        onClick={addAuthorityRow}
                      >
                        <Plus size={12} /> Add person / team
                      </button>
                    )}
                  </div>
                  {procAuthorities.length === 0 ? (
                    editingProcBasic ? (
                      <button
                        type="button"
                        onClick={addAuthorityRow}
                        className="w-full border border-dashed border-gray-200 rounded-lg py-4 text-xs text-gray-400 hover:border-emerald-300 hover:text-emerald-600 transition flex flex-col items-center gap-1"
                      >
                        <Plus size={16} />
                        <span>Add a person or team responsible for this step</span>
                      </button>
                    ) : (
                      <p className="text-xs text-gray-400 italic">No authorities assigned.</p>
                    )
                  ) : (
                    <div className="flex flex-col gap-2">
                      {procAuthorities.map((row, idx) => (
                        <div key={idx} className="flex gap-2 items-center bg-gray-50 rounded-lg px-3 py-2">
                          <div className="flex-1">
                            <FormControl.SearchSelect
                              label=""
                              options={authorities.map((a) => ({ value: String(a.id), label: a.name }))}
                              value={row.authority_id != null ? String(row.authority_id) : ""}
                              disabled={!editingProcBasic}
                              onChange={(v) => updateAuthorityRow(idx, { authority_id: v ? parseInt(String(v)) : null })}
                              clearable
                              placeholder="Who?"
                            />
                          </div>
                          <div className="w-40">
                            <FormControl.SearchSelect
                              label=""
                              options={ROLE_OPTIONS}
                              value={row.role}
                              disabled={!editingProcBasic}
                              onChange={(v) => updateAuthorityRow(idx, { role: (v as StepType) || "PERFORM" })}
                            />
                          </div>
                          {editingProcBasic && (
                            <button
                              type="button"
                              className="text-gray-300 hover:text-red-400 flex-shrink-0 transition"
                              title="Remove"
                              onClick={() => removeAuthorityRow(idx)}
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 3. When */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">3</span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">When</span>
                  </div>
                  <div className={`flex gap-3 ${!editingProcBasic ? "opacity-60 pointer-events-none" : ""}`}>
                    <button
                      type="button"
                      onClick={() => setProcFormData({ ...procFormData, after_step_id: null, timeline_days: null } as any)}
                      className={`flex-1 rounded-lg border-2 px-4 py-3 text-left transition ${(procFormData as any).after_step_id == null ? "border-emerald-400 bg-emerald-50" : "border-gray-200 bg-white hover:border-gray-300"}`}
                    >
                      <div className="text-sm font-semibold text-gray-800">Can start any time</div>
                      <div className="text-[11px] text-gray-400 mt-0.5">No dependency on other steps</div>
                    </button>
                    <button
                      type="button"
                      disabled={afterStepOptions.length === 0}
                      onClick={() => {
                        if (afterStepOptions.length === 0) return;
                        const first = afterStepOptions[0];
                        setProcFormData({
                          ...procFormData,
                          after_step_id: (procFormData as any).after_step_id ?? parseInt(String(first.value)),
                          timeline_anchor: (procFormData as any).timeline_anchor || "AFTER_PREV_END",
                        } as any);
                      }}
                      className={`flex-1 rounded-lg border-2 px-4 py-3 text-left transition ${(procFormData as any).after_step_id != null ? "border-purple-400 bg-purple-50" : afterStepOptions.length === 0 ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed" : "border-gray-200 bg-white hover:border-gray-300"}`}
                    >
                      <div className="text-sm font-semibold text-gray-800">Follows another step</div>
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        {afterStepOptions.length === 0 ? "Add earlier steps first" : "Starts after a predecessor completes"}
                      </div>
                    </button>
                  </div>
                  {(procFormData as any).after_step_id != null && (
                    <div className={`grid grid-cols-3 gap-x-4 gap-y-3 mt-3 ${!editingProcBasic ? "opacity-60 pointer-events-none" : ""}`}>
                      <FormControl.SearchSelect
                        label="After step"
                        options={afterStepOptions}
                        value={String((procFormData as any).after_step_id)}
                        disabled={!editingProcBasic}
                        onChange={(value) => setProcFormData({ ...procFormData, after_step_id: value ? parseInt(String(value)) : null } as any)}
                        clearable
                        placeholder="Choose predecessor"
                      />
                      <FormControl.Input
                        label="Days offset"
                        type="number"
                        value={(procFormData as any).timeline_days ?? ""}
                        disabled={!editingProcBasic}
                        onChange={(e) => setProcFormData({ ...procFormData, timeline_days: e.target.value === "" ? null : parseInt(e.target.value) } as any)}
                        placeholder="e.g. 3"
                      />
                      <FormControl.SearchSelect
                        label="Counted from"
                        options={TIMELINE_ANCHORS}
                        value={(procFormData as any).timeline_anchor || "AFTER_PREV_END"}
                        disabled={!editingProcBasic}
                        onChange={(value) => setProcFormData({ ...procFormData, timeline_anchor: value } as any)}
                      />
                    </div>
                  )}
                </div>

                {/* 4. How — linked audit procedures */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">4</span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">How — audit procedures</span>
                  </div>
                  {selectedProc && selectedProc.draft_id != null && !selectedProc.live_step_id && (
                    <p className="text-xs text-amber-700 mb-2">
                      Audit procedures can be linked to this step once the new version is live.
                    </p>
                  )}
                  {editingProcBasic && !(selectedProc && selectedProc.draft_id != null && !selectedProc.live_step_id) && (
                    <div className="relative mb-2">
                      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-blue-300"
                        placeholder="Search audit procedures…"
                        value={auditSearch}
                        onChange={(e) => setAuditSearch(e.target.value)}
                      />
                    </div>
                  )}
                  {editingProcBasic && auditSearch.trim().length > 0 && (
                    <div className="border border-gray-200 rounded-lg bg-white shadow-sm mb-2 max-h-40 overflow-y-auto">
                      {auditSearchLoading ? (
                        <p className="text-xs text-gray-400 italic px-3 py-2">Searching…</p>
                      ) : auditSearchResults.filter((t) => !procAuditLinks.some((l) => l.procedure_template_id === t.id)).length === 0 ? (
                        <p className="text-xs text-gray-400 italic px-3 py-2">No matching procedures.</p>
                      ) : (
                        auditSearchResults
                          .filter((t) => !procAuditLinks.some((l) => l.procedure_template_id === t.id))
                          .map((t) => (
                            <button
                              key={t.id}
                              type="button"
                              className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 flex items-center justify-between gap-2 border-b border-gray-50 last:border-0"
                              onClick={() => addAuditLink(t)}
                            >
                              <span className="text-gray-800 flex-1 truncate">{t.procedure_name}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 whitespace-nowrap flex-shrink-0">
                                {PROCEDURE_TYPE_LABEL[t.procedure_type] || t.procedure_type}
                              </span>
                            </button>
                          ))
                      )}
                    </div>
                  )}
                  {procAuditLinks.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No audit procedures linked yet.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {procAuditLinks.map((link, idx) => (
                        <div key={idx} className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2 mb-1.5">
                            <FileText size={12} className="text-blue-400 flex-shrink-0" />
                            <span className="text-xs text-gray-800 flex-1 truncate font-medium">{link.procedure_name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-blue-100 text-blue-600 whitespace-nowrap flex-shrink-0">
                              {PROCEDURE_TYPE_LABEL[link.procedure_type] || link.procedure_type}
                            </span>
                            {editingProcBasic && (
                              <button type="button" className="text-gray-300 hover:text-red-400 flex-shrink-0 transition" onClick={() => removeAuditLink(idx)}>
                                <X size={12} />
                              </button>
                            )}
                          </div>
                          <div className={`pl-4 ${!editingProcBasic ? "opacity-60 pointer-events-none" : ""}`}>
                            <FormControl.SearchSelect
                              label=""
                              options={[{ value: "", label: "— No authority assigned —" }, ...authorities.map((a) => ({ value: String(a.id), label: a.name }))]}
                              value={link.authority_id != null ? String(link.authority_id) : ""}
                              onChange={(v) => updateAuditLink(idx, { authority_id: v ? Number(v) : null })}
                              placeholder="Authority who runs this procedure…"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </form>
            </div>
          )}

          {/* ── Configuration tab (3-column) ──────────────────────────── */}
          {configTab === "configure" && activeProcedureForConfig && (
            <>
              <div className="px-6 py-3 border-b border-gray-100 flex justify-center">
                <div className="bg-green-50 border-2 border-green-200 rounded-xl py-2 px-6 relative text-center shadow-sm">
                  <div className="text-[10px] font-bold text-green-600 uppercase tracking-wider">
                    Procedure Step
                  </div>
                  <div className="text-base font-bold text-gray-800 leading-tight">
                    {activeProcedureForConfig.step_name}
                  </div>
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white text-green-500 rounded-full border-2 border-green-200 p-0.5 z-10 shadow-sm">
                    <ChevronDown size={14} strokeWidth={3} />
                  </div>
                </div>
              </div>
              {!liveIdOf(activeProcedureForConfig) ? (
                <div className="p-6 text-sm text-gray-500" data-testid="NOTE_sop_step_controls_after_live">
                  This step is new in the draft. Controls can be linked to it once the new version is live.
                </div>
              ) : (
              <div
                className="grid grid-cols-3 divide-x divide-gray-200"
                style={{ minHeight: "400px" }}
              >
                <RisksColumn
                  selectedControl={selectedControl}
                  risks={controlRisks}
                  loading={controlRisksLoading}
                  onRiskClick={handleRiskClick}
                />
                <ControlsColumn
                  controls={stepControls}
                  selectedControlId={selectedControl?.id}
                  loading={stepControlsLoading}
                  readOnly={sopTab === "live"}
                  onSelectControl={handleSelectControl}
                  onAddControl={handleAddControl}
                  onUnlinkControl={handleUnlinkControl}
                />
                <ImplementationsColumn
                  selectedControl={selectedControl}
                  implementations={controlImplementations}
                  loading={controlImplementationsLoading}
                />
              </div>
              )}
            </>
          )}
        </Modal.Body>

        {/* Footer — only for Basic Details tab */}
        {configTab === "edit" && selectedProc && stepEditable(selectedProc) && (
          <Modal.Footer className="app-modal-footer--split">
            <Button
              variant={selectedProc.is_active === 1 ? "inactive-outline" : "active-outline"}
              icon={selectedProc.is_active === 1 ? CircleMinus : CircleCheck}
              onClick={handleToggleStepActive}
              disabled={toggling || saving}
            >
              {toggling ? "..." : selectedProc.is_active === 1 ? "Inactive" : "Active"}
            </Button>
            <div className="flex gap-2">
              {editingProcBasic ? (
                <Button
                  variant="update"
                  icon={Check}
                  onClick={() => procFormRef.current?.requestSubmit()}
                  disabled={saving || toggling}
                >
                  {saving ? "Updating..." : "Update"}
                </Button>
              ) : (
                <Button
                  variant="fill"
                  icon={Edit2}
                  onClick={() => setEditingProcBasic(true)}
                  disabled={toggling}
                >
                  Edit
                </Button>
              )}
            </div>
          </Modal.Footer>
        )}
      </Modal>

      {/* Control Picker — pick an existing entity control to associate with
          the active step. Soft-deleted / already-linked controls are filtered
          out client-side before showing. */}
      <Modal
        isOpen={showControlPicker}
        onClose={() => setShowControlPicker(false)}
        size="lg"
      >
        <Modal.Header>
          {activeProcedureForConfig ? (
            <>
              Link a control to step{" "}
              <span className="item-highlight">{activeProcedureForConfig.step_name}</span>
            </>
          ) : (
            "Link a control"
          )}
        </Modal.Header>
        <Modal.Body>
          <div className="netbody-header">
            <FormControl.Input
              type="text"
              placeholder="Search controls by name or code…"
              value={pickerSearch}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPickerSearch(e.target.value)}
              leadingIcon={<Search size={14} />}
              className="filter-search"
              data-testid="INP_sop_picker_search"
            />
          </div>
          <div className="table-list" style={{ marginTop: 8 }}>
            {pickerLoading ? (
              <div className="text-center py-8 text-gray-400 italic">Loading controls…</div>
            ) : (() => {
              const term = pickerSearch.trim().toLowerCase();
              const visible = term
                ? pickerControls.filter(c =>
                    (c.control_code || "").toLowerCase().includes(term) ||
                    (c.control_name || "").toLowerCase().includes(term))
                : pickerControls;
              if (visible.length === 0) {
                return (
                  <NoDataFound
                    title="No matching controls"
                    message={term ? "Try a different search term." : "All active controls are already linked."}
                  />
                );
              }
              return (
                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <thead>
                      <tr>
                        <th className="table-list-th table-list-cell--sno">S.No</th>
                        <th className="table-list-th">Code</th>
                        <th className="table-list-th">Name</th>
                        <th className="table-list-th table-list-cell--truncate">Description</th>
                        <th className="table-list-th">Type</th>
                        <th className="table-list-th" style={{ width: 100 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((c, idx) => (
                        <tr key={c.id} className="table-list-row" data-testid={`ROW_sop_picker_${c.id}`}>
                          <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                          <td className="table-list-cell text-xs">{c.control_code}</td>
                          <td className="table-list-cell">
                            <span className="table-list-cell-title">{c.control_name}</span>
                          </td>
                          <td className="table-list-cell table-list-cell--truncate" title={c.control_description || ""}>
                            {c.control_description || "—"}
                          </td>
                          <td className="table-list-cell">{c.control_type || "—"}</td>
                          <td className="table-list-cell">
                            <Button
                              variant="fill"
                              icon={Plus}
                              onClick={() => handlePickerLink(c)}
                              disabled={pickerLinking === c.id}
                              data-testid={`BTN_sop_picker_link_${c.id}`}
                            >
                              {pickerLinking === c.id ? "Linking…" : "Link"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </Modal.Body>
      </Modal>

      {/* Risk Visualization Modal */}
      <Modal
        isOpen={showRiskVisModal}
        onClose={() => setShowRiskVisModal(false)}
        size="lg"
      >
        <Modal.Header>Risk Visualization</Modal.Header>
        <Modal.Body className="p-0">
          {selectedRiskForVis && (
            <RiskVisualization
              risk={{
                id: selectedRiskForVis.id,
                risk_code: selectedRiskForVis.code,
                risk_name: selectedRiskForVis.name,
              }}
              onClose={() => setShowRiskVisModal(false)}
            />
          )}
        </Modal.Body>
      </Modal>

      <WorkflowModal
        isOpen={!!workflowRow}
        onClose={() => { setWorkflowRow(null); loadProcesses(); }}
        kind={WF_KIND}
        id={workflowRow?.id ?? null}
        onChanged={showModal ? refreshSelectedProcess : loadProcesses}
      />

      {/* Approval workflow of the draft of the next version. Approving it makes
          it the live version (server-side, in one transaction). */}
      <WorkflowModal
        isOpen={draftWorkflowOpen && !!sopDraft}
        onClose={() => setDraftWorkflowOpen(false)}
        kind={SOP_DRAFT_WF_KIND}
        id={sopDraft?.id ?? null}
        onChanged={async () => {
          const wasVersion = sopDraft?.target_version_number;
          await refreshSelectedProcess();
          setDraftChanges(null);
          const { draft } = selectedProcess
            ? await sopVersioningService.getDraft(selectedProcess.id)
            : { draft: null };
          if (!draft && wasVersion) {
            setDraftWorkflowOpen(false);
            setToast({ type: "success", message: `Version ${wasVersion} is now live.` });
            setSopTab("versions");
          }
        }}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default GovernanceProcesses;

